import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { getAuth } from '@angular/fire/auth';
import { MatDialog } from '@angular/material/dialog';
import {
    AccountMapper,
    AiFixerDelegate,
    AnnualReportMetaInformationDTO,
    AnnualReportProvider,
    AumicoSections,
    DatevAiAdapter,
    FinancialStatementImportFormPage,
    TaxonomyDefinitions,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AiAgentJobRequest, AiAgentProvider } from '@modeso/aumico-lib-ai-agents-fe';
import {
    AumicoCompaniesService,
    BexioBusinessYear,
    BexioConnectionStatusDto,
    BexioImportResult,
    BexioProvider,
    BexioState,
} from '@modeso/aumico-lib-companies-fe';
import {
    IAbacusImportBody,
    ITopalImportBody,
    IAccountoImportBody,
    IProffixImportBody,
    IFiscalYear,
    IJournalImportResult,
    IntegrationProvider,
    IntegrationVendor,
    VENDOR_DEFINITIONS,
    VendorDefinition,
    accountoImportResultForReport,
    proffixImportResultForReport,
} from '@modeso/aumico-lib-integrations-fe';
import {
    AumGlobalAction,
    AumicoCoreLayoutProvider,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    IAumGlobalActionEvent,
} from '@modeso/aumico-lib-core-fe';
import { GetOrganizationDto, OrganizationProvider, OrganizationSettings } from '@modeso/aumico-lib-users-fe';
import {
    AccountType,
    AnnualReportStep,
    AutofillOptions,
    FinancialStatementTypes,
    IAccount,
    IAccountMappingTemplate,
    IAnnualReport,
    IBalanceSwitchAccount,
    IExtraExcelContent,
    IFinStatements,
    IFinancialStatementSource,
    NumberFormats,
    Signs,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ISpreadSheet } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/ISpreadSheet';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep, omit } from 'lodash-es';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import { filter, find, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { TAXONAMY } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/constants/autofill-options';
import { Constants } from '../../../../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { MixpanelService } from '../../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../../mixpanel/mixpanelEvents.enum';
import { Paths } from '../../../../enums/paths.enum';
import { templateConstants } from '../../../../shared/template-constants';
import { AppStateDispatcher } from '../../../../state/app.state.dispatcher';
import { FinancialStatementSourceMap } from '../../../../types/financial-statement-source-map.type';
import { AnnualReportSubPage } from '../../annual-report-sub-page';
import { ImportService } from '../../services/import.service';

const debug = Debug('aumico:saas:FinancialStatementImportBalanceSheetComponent');

/** How many account numbers the unmappable-accounts warning lists before it truncates. */
const UNMAPPABLE_ACCOUNTS_DISPLAY_LIMIT = 10;

/**
 * View state for the import-summary warning about accounts the vendor could not assign to a
 * statement. Precomputed when the import result arrives so the template stays free of logic.
 */
interface IUnmappableAccounts {
    /** Total number of affected accounts — may exceed the numbers listed in `accountNumbers`. */
    count: number;
    /** Comma-separated account numbers, truncated at UNMAPPABLE_ACCOUNTS_DISPLAY_LIMIT. */
    accountNumbers: string;
}

@UntilDestroy()
@Component({
    selector: 'aumico-financial-statement-import-balance-sheet-component',
    templateUrl: 'financial-statement-import-balanceSheet.component.html',
    styleUrls: ['financial-statement-import-balanceSheet.component.scss'],
})
export class FinancialStatementImportBalanceSheetComponent extends AnnualReportSubPage implements OnInit {
    @ViewChild(FinancialStatementImportFormPage)
    private financialStatementImportFormPage?: FinancialStatementImportFormPage;

    activeAnnualReport: IAnnualReport | undefined;
    activeFinStatements: IFinStatements[] | undefined;
    activeMetaInfo: AnnualReportMetaInformationDTO | undefined;

    balanceIndexInFinStatementsArray = 0;
    incomeIndexInFinStatementsArray = 1;
    isAutoFillApplied: boolean;
    fileChanged = false;
    /** True when an Excel file has been selected and not yet saved — cleared after save or Bexio import. */
    private excelFileUploaded = false;
    /** Set to the import timestamp when a Bexio import was performed and not yet saved. */
    private bexioImportedAt: Date | null = null;
    noPreviousYearData: boolean;
    isPreviousYearDataTakenFromPreviousBalanceAnnualReport: boolean;
    isPreviousYearDataTakenFromPreviousIncomeAnnualReport: boolean;
    previousBalanceAccounts: IAccount[] = [];
    previousIncomeAccounts: IAccount[] = [];
    previousBalanceSwitchAccounts: IBalanceSwitchAccount[] = [];

    // for the upload and mapping autofill selection list dialog
    selectedAccountingBasisId: string | undefined;
    selectedCompanyLegalFormId: string | undefined;
    uploadAutofillWithOptions: Record<AutofillOptions, string> | undefined;
    uploadAndMappingAutoFillAnnualReportSubject = new Subject();
    uploadAndMappingAutoFillAnnualReportIdExist = false;
    uploadAndMappingAutoFillAnnualReportToggleDisabled: boolean;
    extraExcelContent: IExtraExcelContent | null = null;

    private readonly autoFillFinStatementsCombined$: Observable<IFinStatements[]>;

    private balanceAccounts: IAccount[] = [];
    private incomeAccounts: IAccount[] = [];
    /**
     * {
     *  balance: IFinancialStatementSource,
     *  income: IFinancialStatementSource,
     *  x: IFinancialStatementSource.....
     * }
     */
    financialStatementSource: FinancialStatementSourceMap = {} as FinancialStatementSourceMap;

    private isSaveRequestInProgress = false;

    readonly mappingStepLabel = Constants.ACTION_BAR.LABELS.ACCOUNT_MAPPING_KEY;
    readonly mappingStepPath = Paths.MAPPING;

    isPartOfArNavigation$: Observable<boolean>;

    currentOrganizationSettings: OrganizationSettings | undefined;
    showShareholderInformation = false;
    reportLocale: string;
    showManualUpload = false;

    bexioConnectionStatus$: Observable<BexioState['connectionStatus']>;
    bexioConnection$: Observable<BexioConnectionStatusDto | null>;
    bexioBusinessYears$: Observable<BexioBusinessYear[]>;
    bexioBusinessYearsLoading$: Observable<boolean>;
    bexioImportStatus$: Observable<BexioState['importStatus']>;
    bexioImportResult$: Observable<BexioImportResult | null>;
    bexioImportError$: Observable<HttpErrorResponse | null>;
    /** Snapshot of the Bexio business years list — kept in sync via subscription so that
     *  onBexioImportRequested can synchronously resolve the previous year's ID. */
    private bexioBusinessYears: BexioBusinessYear[] = [];
    private bexioConnectionStatusValue: BexioState['connectionStatus'] = 'idle';

    // ── Bexio Import Summary ─────────────────────────────────────────────
    showBexioSummaryModal = false;
    bexioSummaryResult: BexioImportResult | null = null;
    bexioSummaryCompanyName: string | null = null;
    private bexioSyncedPreviousYearId: number | null = null;

    // ── D365 BC Import (mirror Bexio shape) ──────────────────────────────
    d365bcFiscalYears$: Observable<IFiscalYear[]>;
    d365bcImportStatus$: Observable<'idle' | 'loading' | 'success' | 'error'>;
    d365bcImportResult$: Observable<IJournalImportResult | null>;
    /** Card status mapped from connection state. Only idle/connected are ever produced here. */
    d365bcCardStatus: 'idle' | 'connected' = 'idle';
    /** Snapshot of the fiscal years list so `onD365bcImportRequested` can resolve the previous year synchronously. */
    private d365bcFiscalYears: IFiscalYear[] = [];
    /** Cached mapping for the active Company so handlers can fire without re reading nested company state. */
    private d365bcMapping: { connectionId: string; externalCompanyId: string } | null = null;

    showD365BcSummaryModal = false;
    /** "Manage in Organization Settings" hint modal for per-org vendors (D365 BC) shown when nothing is linked. */
    showD365bcOrgModal = false;
    /** Vendor definition for the BC go-to-admin modal (title + name). */
    readonly d365bcVendorDef: VendorDefinition | undefined = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.D365_BC,
    );
    /** True when the user is an org/app admin — only they can reach the Org Settings. */
    isAdmin$: Observable<boolean>;
    d365bcSummaryResult: IJournalImportResult | null = null;
    d365bcSummaryCompanyName: string | null = null;
    /** Accounts D365 BC could not assign to a statement — null when there are none. */
    d365bcUnmappableAccounts: IUnmappableAccounts | null = null;
    /** Public for template binding (summary modal renders the previous period row). */
    d365bcSyncedPreviousFiscalYear: IFiscalYear | null = null;
    /** Set to the import timestamp when a D365 BC import was performed and not yet saved. */
    private d365bcImportedAt: Date | null = null;

    // ── Abacus Import (mirrors D365 BC, minus fiscal-year discovery) ──────
    // Abacus has no `/fiscal-years` endpoint. The FE pulls the period dates
    // from the active Annual Report's General Info and only sends the previous
    // year pair when the AR does NOT have an autofill report linked.
    abacusImportStatus$: Observable<'idle' | 'loading' | 'success' | 'error'>;
    abacusImportResult$: Observable<IJournalImportResult | null>;
    abacusCardStatus: 'idle' | 'connected' = 'idle';
    private abacusMapping: { connectionId: string; externalCompanyId: string } | null = null;
    showAbacusSummaryModal = false;
    showAbacusOrgModal = false;
    readonly abacusVendorDef: VendorDefinition | undefined = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.ABACUS,
    );
    abacusSummaryResult: IJournalImportResult | null = null;
    abacusSummaryCompanyName: string | null = null;
    /** Accounts Abacus could not assign to a statement — null when there are none. */
    abacusUnmappableAccounts: IUnmappableAccounts | null = null;
    /** Period the import was requested for: derived from AR General Info, displayed in summary. */
    abacusSummaryPeriod: { from: string; to: string } | null = null;
    abacusSummaryPreviousPeriod: { from: string; to: string } | null = null;
    /** Set to the import timestamp; the save-flow will pick this up once the BE type release adds `abacus` to IAnnualReport.integrations. */
    private abacusImportedAt: Date | null = null;

    // ── Topal Import (mirrors Abacus — on-prem, no fiscal-year discovery) ─────
    topalImportStatus$: Observable<'idle' | 'loading' | 'success' | 'error'>;
    topalImportResult$: Observable<IJournalImportResult | null>;
    topalCardStatus: 'idle' | 'connected' = 'idle';
    private topalMapping: { connectionId: string; externalCompanyId: string } | null = null;
    showTopalSummaryModal = false;
    showTopalOrgModal = false;
    readonly topalVendorDef: VendorDefinition | undefined = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.TOPAL,
    );
    topalSummaryResult: IJournalImportResult | null = null;
    topalSummaryCompanyName: string | null = null;
    topalSummaryPeriod: { from: string; to: string } | null = null;
    topalSummaryPreviousPeriod: { from: string; to: string } | null = null;
    private topalImportedAt: Date | null = null;

    // ── Accounto Import (mirrors Topal — dates from General Info, no discovery) ─
    accountoImportStatus$: Observable<'idle' | 'loading' | 'success' | 'error'>;
    accountoCardStatus: 'idle' | 'connected' = 'idle';
    private accountoMapping: { connectionId: string; externalCompanyId: string } | null = null;
    showAccountoSummaryModal = false;
    showAccountoOrgModal = false;
    readonly accountoVendorDef: VendorDefinition | undefined = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.ACCOUNTO,
    );
    accountoSummaryResult: IJournalImportResult | null = null;
    accountoSummaryCompanyName: string | null = null;
    accountoSummaryPeriod: { from: string; to: string } | null = null;
    accountoSummaryPreviousPeriod: { from: string; to: string } | null = null;
    private accountoImportedAt: Date | null = null;

    // ── Proffix Import (mirrors Topal — dates from General Info, no discovery) ─
    proffixImportStatus$: Observable<'idle' | 'loading' | 'success' | 'error'>;
    proffixCardStatus: 'idle' | 'connected' = 'idle';
    private proffixMapping: { connectionId: string; externalCompanyId: string } | null = null;
    showProffixSummaryModal = false;
    showProffixOrgModal = false;
    readonly proffixVendorDef: VendorDefinition | undefined = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.PROFFIX,
    );
    proffixSummaryResult: IJournalImportResult | null = null;
    proffixSummaryCompanyName: string | null = null;
    proffixSummaryPeriod: { from: string; to: string } | null = null;
    proffixSummaryPreviousPeriod: { from: string; to: string } | null = null;
    private proffixImportedAt: Date | null = null;

    private importedTaxonomy: TaxonomyDefinitions | undefined;
    userLanguage: string;

    // ── AI Fixer ──────────────────────────────────────────────────────
    aiJobRequest: AiAgentJobRequest | null = null;
    aiOriginalSections: AumicoSections | null = null;
    aiFixedSections: AumicoSections | null = null;
    showAiPanel = false;
    firebaseToken = '';
    /** Original spreadsheet kept so we can recover SpecialFLIs on accept */
    private aiOriginalSpreadSheet: ISpreadSheet | null = null;
    /**
     * Taxonomy built after the user accepts an AI fix.
     * Included in the aiDelegate.fixedTaxonomy and drives the "Fixed with AI" badge.
     */
    aiAcceptedTaxonomy: TaxonomyDefinitions | null = null;
    /** Single delegate object passed to the child form page, bundling all AI state + callbacks. */
    aiDelegate: AiFixerDelegate | null = null;

    constructor(
        injector: Injector,
        private readonly annualReportProvider: AnnualReportProvider,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override actionsListener$: ActionsSubject,
        private readonly appStateDispatcher: AppStateDispatcher,
        private readonly mixpanelService: MixpanelService,
        private readonly organizationProvider: OrganizationProvider,
        private readonly importService: ImportService,
        private readonly accountMapper: AccountMapper,
        private readonly datevAiAdapter: DatevAiAdapter,
        private readonly bexioProvider: BexioProvider,
        private readonly integrationProvider: IntegrationProvider,
        private readonly companiesService: AumicoCompaniesService,
        private readonly aiAgentProvider: AiAgentProvider,
        dialog: MatDialog,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.isAdmin$ = this.authProvider.getIsAdmin$();

        this.importService.reset();
        this.currentAnnualReportStep = AnnualReportStep.IMPORT_BALANCE_INCOME;

        this.bexioConnectionStatus$ = this.bexioProvider.getConnectionStatus$();
        this.bexioConnection$ = this.bexioProvider.getConnection$();
        this.bexioBusinessYears$ = this.bexioProvider.getBusinessYears$();
        this.bexioBusinessYearsLoading$ = this.bexioProvider.getBusinessYearsLoading$();
        this.bexioImportStatus$ = this.bexioProvider.getImportStatus$();
        this.bexioImportResult$ = this.bexioProvider.getImportResult$();
        this.bexioImportError$ = this.bexioProvider.getImportError$();

        this.d365bcFiscalYears$ = this.integrationProvider.getD365BcFiscalYears$();
        this.d365bcImportStatus$ = this.integrationProvider.getD365BcImportStatus$();
        this.d365bcImportResult$ = this.integrationProvider.getD365BcImportResult$();

        this.abacusImportStatus$ = this.integrationProvider.getAbacusImportStatus$();
        this.abacusImportResult$ = this.integrationProvider.getAbacusImportResult$();
        this.topalImportStatus$ = this.integrationProvider.getTopalImportStatus$();
        this.topalImportResult$ = this.integrationProvider.getTopalImportResult$();
        this.accountoImportStatus$ = this.integrationProvider.getAccountoImportStatus$();
        this.proffixImportStatus$ = this.integrationProvider.getProffixImportStatus$();

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(
            AnnualReportStep.IMPORT_BALANCE_INCOME,
        );

        this.autoFillFinStatementsCombined$ = this.annualReportProvider.getCombinedAutofillFinStatements$().pipe(
            filter((autofillFinStatements: IFinStatements[]) => autofillFinStatements != null),
            untilDestroyed(this),
        );

        this.userLanguage = this.i18n.language.toLowerCase();
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.UPLOAD_SHEET_TIME_SPENT_TILL_COMPLETION);
        super.ngOnInit();

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.autoFillFinStatementsCombined$
            .pipe(untilDestroyed(this))
            .subscribe((autofillFinStatements: IFinStatements[]) => {
                this.assignPreviousAccounts(autofillFinStatements);
                // Only pre-fill sources from autofill data when there are no saved finStatements yet
                // AND no save is currently in progress.
                // Once the user has saved their own values, we must not overwrite them every time
                // the autofill store re-emits (e.g. after each save triggers a re-dispatch of the
                // autofill annual report id from handleGetActiveAnnualReport).
                // The isSaveRequestInProgress guard prevents autofill from overwriting the
                // user's values during the synchronous enforceGlobalStateUpdate phase of a save,
                // which re-triggers the autofill selector before handleGetActiveAnnualReport has
                // had a chance to update activeFinStatements with the newly saved data.
                if (!this.isSaveRequestInProgress && (this.activeFinStatements == null || this.activeFinStatements.length === 0)) {
                    this.assignBalanceAndIncomeSources(autofillFinStatements);
                }
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => {
                if (this.isSaveRequestInProgress) {
                    this.onSaveSuccessfully();
                }
            });
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        this.annualReportProvider
            .getActiveMetaInformation$()
            .pipe(untilDestroyed(this))
            .subscribe((metaInfo) => {
                this.activeMetaInfo = metaInfo;
            });

        // Keep a local snapshot of Bexio business years so onBexioImportRequested
        // can synchronously resolve the previous year's ID without async complexity.
        this.bexioBusinessYears$.pipe(untilDestroyed(this)).subscribe((years) => {
            this.bexioBusinessYears = years ?? [];
        });

        this.bexioConnectionStatus$.pipe(untilDestroyed(this)).subscribe((status) => {
            this.bexioConnectionStatusValue = status;
        });

        this.bexioConnectionStatus$
            .pipe(
                filter((status) => status === 'connected'),
                untilDestroyed(this),
            )
            .subscribe(() => {
                const companyId = this.activeAnnualReport?.generalInformation?.company?._id;
                if (companyId != null) {
                    this.bexioProvider.loadBusinessYears(companyId);
                }
            });

        // When a Bexio import completes successfully, process the result directly
        this.bexioImportStatus$
            .pipe(
                filter((status) => status === 'success'),
                withLatestFrom(this.bexioImportResult$, this.bexioConnection$),
                untilDestroyed(this),
            )
            .subscribe(([, result, connection]) => {
                if (result != null) {
                    this.onBexioImportSucceeded(result);
                    this.bexioSummaryResult = result;
                    this.bexioSummaryCompanyName = connection?.bexioCompanyName ?? null;
                    this.showBexioSummaryModal = true;
                }
            });

        // Mirror Bexio: snapshot of the D365 BC fiscal years so the sync handler
        // can resolve the previous year synchronously.
        this.d365bcFiscalYears$.pipe(untilDestroyed(this)).subscribe((years) => {
            this.d365bcFiscalYears = years ?? [];
        });

        // When a D365 BC import completes successfully, mirror the Bexio handling
        // pattern: process the result + open summary modal.
        this.d365bcImportStatus$
            .pipe(
                filter((status) => status === 'success'),
                withLatestFrom(this.d365bcImportResult$),
                untilDestroyed(this),
            )
            .subscribe(([, result]) => {
                if (result != null) {
                    this.onD365bcImportSucceeded(result);
                    this.d365bcSummaryResult = result;
                    this.d365bcSummaryCompanyName =
                        this.activeAnnualReport?.generalInformation?.company?.name ?? null;
                    this.showD365BcSummaryModal = true;
                }
            });

        // Same pattern for Abacus. The result shape is shared (IJournalImportResult),
        // so the processing path can reuse the D365 BC variant verbatim — we only
        // need separate state vars + summary modal toggle per vendor.
        this.abacusImportStatus$
            .pipe(
                filter((status) => status === 'success'),
                withLatestFrom(this.abacusImportResult$),
                untilDestroyed(this),
            )
            .subscribe(([, result]) => {
                if (result != null) {
                    this.onAbacusImportSucceeded(result);
                    this.abacusSummaryResult = result;
                    this.abacusSummaryCompanyName =
                        this.activeAnnualReport?.generalInformation?.company?.name ?? null;
                    this.showAbacusSummaryModal = true;
                }
            });

        // Same pattern for Topal.
        this.topalImportStatus$
            .pipe(
                filter((status) => status === 'success'),
                withLatestFrom(this.topalImportResult$),
                untilDestroyed(this),
            )
            .subscribe(([, result]) => {
                if (result != null) {
                    this.onTopalImportSucceeded(result);
                    this.topalSummaryResult = result;
                    this.topalSummaryCompanyName =
                        this.activeAnnualReport?.generalInformation?.company?.name ?? null;
                    this.showTopalSummaryModal = true;
                }
            });

        // Accounto: a result is taken only when it was run for the report on screen. The slice is global and
        // the import outlives this step, so a late success (or one still in the store) for another report
        // must not land here (frontend guidelines §4: late results check where the user is now).
        this.integrationProvider
            .getAccountoJournalImportState$()
            .pipe(
                map((slice) =>
                    accountoImportResultForReport(slice, {
                        companyId: this.activeAnnualReport?.generalInformation?.company?._id,
                        annualReportId: this.activeAnnualReport?._id,
                    }),
                ),
                filter((result): result is IJournalImportResult => result != null),
                untilDestroyed(this),
            )
            .subscribe((result) => {
                this.onAccountoImportSucceeded(result);
                this.accountoSummaryResult = result;
                this.accountoSummaryCompanyName = this.activeAnnualReport?.generalInformation?.company?.name ?? null;
                this.showAccountoSummaryModal = true;
            });

        // Proffix: a result is taken only when it was run for the report on screen. The slice is global and
        // the import outlives this step, so a late success (or one still in the store) for another report
        // must not land here (frontend guidelines §4: late results check where the user is now).
        this.integrationProvider
            .getProffixJournalImportState$()
            .pipe(
                map((slice) =>
                    proffixImportResultForReport(slice, {
                        companyId: this.activeAnnualReport?.generalInformation?.company?._id,
                        annualReportId: this.activeAnnualReport?._id,
                    }),
                ),
                filter((result): result is IJournalImportResult => result != null),
                untilDestroyed(this),
            )
            .subscribe((result) => {
                this.onProffixImportSucceeded(result);
                this.proffixSummaryResult = result;
                this.proffixSummaryCompanyName = this.activeAnnualReport?.generalInformation?.company?.name ?? null;
                this.showProffixSummaryModal = true;
            });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                find((currentOrganization: GetOrganizationDto) => currentOrganization != null),
                tap((currentOrganization) => {
                    this.currentOrganizationSettings = currentOrganization.settings;
                }),
                mergeMap(() =>
                    this.annualReportProvider.getActiveAnnualReport$().pipe(
                        filter(
                            (activeAnnual: IAnnualReport) =>
                                activeAnnual != null && activeAnnual.generalInformation != null,
                        ),
                        withLatestFrom(this.annualReportProvider.getActiveOriginalFinStatements$()),

                        tap(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) =>
                            this.handleGetActiveAnnualReport(activeAnnualReport, finStatements),
                        ),
                        mergeMap(() => this.uploadAndMappingAutoFillAnnualReportSubject),
                        mergeMap(() => {
                            if (
                                this.uploadAutofillWithOptions == null ||
                                this.uploadAutofillWithOptions[TAXONAMY] == null
                            ) {
                                return of(null);
                            }

                            return this.annualReportProvider.getSelectedYearForUploadAndMappingAutofillFinStatements$();
                        }),
                        tap((autofillFinStatements) => {
                            if (autofillFinStatements != null) {
                                this.isAutoFillApplied = true;
                                this.assignBalanceAndIncomeSources(autofillFinStatements);
                            } else {
                                this.assignBalanceAndIncomeSources(this.activeFinStatements);
                            }
                        }),
                        filter(
                            (selectedAnnualReportForAutoFill) =>
                                this.activeFinStatements.length === 0 && selectedAnnualReportForAutoFill == null,
                        ),
                        mergeMap(() => this.autoFillFinStatementsCombined$),
                        untilDestroyed(this),
                    ),
                ),
                untilDestroyed(this),
            )
            .subscribe((autofillFinStatements: IFinStatements[]) => {
                if (!this.isAutoFillApplied) {
                    this.assignBalanceAndIncomeSources(autofillFinStatements);
                    this.assignPreviousAccounts(autofillFinStatements);
                    this.isAutoFillApplied = true;
                }
            });
    }

    /**
     * REV-21304: returns the trustworthy previous-year closing to carry into the current year's opening for a
     * balance switch account.
     *
     * Reconstruct from the account's split LEGS first — they always hold the current year's magnitudes,
     * so they give the correct signed closing and are immune to BOTH the stale `originalClosingBalance`
     * (frozen at the first split and propagated unchanged across years — the 3rd-year carry bug) AND the
     * double-save sign corruption of the summary's own `calculated`/`closing`. Only when the legs are
     * absent do we fall back to `originalClosingBalance` (raw signed value captured at split time), then
     * `calculatedClosingBalance` with the interpretation (import-options) sign reversed, then the
     * possibly-corrupted `closingBalance`.
     */
    private reliableSwitchPreviousClosing(switchAcc: IBalanceSwitchAccount): number {
        const fromLegs = this.switchClosingFromLegs(switchAcc);
        if (fromLegs != null) {
            return fromLegs;
        }
        if (switchAcc.originalClosingBalance != null && Number.isFinite(switchAcc.originalClosingBalance)) {
            return switchAcc.originalClosingBalance;
        }
        if (switchAcc.calculatedClosingBalance != null && Number.isFinite(switchAcc.calculatedClosingBalance)) {
            return switchAcc.interpretation === Signs.NEGATIVE
                ? -1 * switchAcc.calculatedClosingBalance
                : switchAcc.calculatedClosingBalance;
        }
        return switchAcc.closingBalance;
    }

    /**
     * REV-21304 (3rd-year carry): reconstruct a switch account's signed closing from its two split legs
     * in the previous-year balance accounts (mirrors BalanceSwitchAccountService's leg-combining). The
     * legs store positive magnitudes on their own side: an asset-origin account nets asset − liability,
     * a liability-origin one nets liability − asset; the interpretation is then reversed to recover the
     * raw signed value. Returns null when neither leg is present so the caller can fall back.
     */
    private switchClosingFromLegs(switchAcc: IBalanceSwitchAccount): number | null {
        if (!Array.isArray(this.previousBalanceAccounts)) {
            return null;
        }
        const legs = this.previousBalanceAccounts.filter(
            (a) =>
                a?.accountNumber === switchAcc.accountNumber &&
                (a as IBalanceSwitchAccount).isBalanceSwitchAccount === true,
        ) as IBalanceSwitchAccount[];
        const assetLeg = legs.find((l) => l.originsFromAsset === true);
        const liabLeg = legs.find((l) => l.originsFromAsset === false);
        if (assetLeg == null && liabLeg == null) {
            return null;
        }
        const mag = (leg: IBalanceSwitchAccount | undefined): number => {
            if (leg == null) {
                return 0;
            }
            if (leg.calculatedClosingBalance != null && Number.isFinite(leg.calculatedClosingBalance)) {
                return Math.abs(leg.calculatedClosingBalance);
            }
            return Math.abs(leg.closingBalance ?? 0);
        };
        const combinedClosing =
            switchAcc.originsFromAsset === true ? mag(assetLeg) - mag(liabLeg) : mag(liabLeg) - mag(assetLeg);
        return switchAcc.interpretation === Signs.NEGATIVE ? -1 * combinedClosing : combinedClosing;
    }

    onAccountsToMap(accounts: IAccount[]) {
        if (this.fileChanged && accounts.length === 0) {
            // only allow emptying of accounts upon file change(deletion)
            this.balanceAccounts = [];
            this.incomeAccounts = [];
        } else if (accounts.length !== 0) {
            this.balanceAccounts = accounts.filter((account) => account.type === AccountType.BALANCE);
            this.incomeAccounts = accounts.filter((account) => account.type === AccountType.INCOME);
        }
    }

    onBexioImportRequested(businessYearId: number): void {
        const companyId = this.activeAnnualReport?.generalInformation?.company?._id;
        const annualReportId = this.activeAnnualReport?._id;
        if (companyId == null || annualReportId == null) {
            return;
        }

        // When previous year data is active AND it is NOT sourced from another Aumico annual report,
        // Bexio must also supply the previous year's closing balances so they can be used as
        // opening balances in the mapping step.  We find the business year immediately before the
        // selected one (closest end date that is still before the selected year's start date).
        let previousBusinessYearId: number | undefined;
        const needsPreviousYearFromBexio =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;

        if (needsPreviousYearFromBexio && this.bexioBusinessYears.length > 1) {
            const selectedYear = this.bexioBusinessYears.find((y) => y.id === businessYearId);
            if (selectedYear != null) {
                const selectedStart = new Date(selectedYear.start).getTime();
                // Candidates: all years whose end date is before the selected year's start date
                const candidates = this.bexioBusinessYears.filter(
                    (y) => y.id !== businessYearId && new Date(y.end).getTime() < selectedStart,
                );
                // Pick the one with the latest end date (i.e. the immediately preceding year)
                if (candidates.length > 0) {
                    const prevYear = candidates.reduce((latest, y) =>
                        new Date(y.end).getTime() > new Date(latest.end).getTime() ? y : latest,
                    );
                    previousBusinessYearId = prevYear.id;
                }
            }
        }

        this.bexioSyncedPreviousYearId = previousBusinessYearId ?? null;
        this.bexioProvider.importData(companyId, annualReportId, businessYearId, previousBusinessYearId, this.activeMetaInfo?._id);
    }

    onManualUploadRequested(): void {
        this.showManualUpload = true;
        this.bexioProvider.resetImport();
        setTimeout(() => {
            const el = document.querySelector('aumico-annualReport-financial-statement-import-form-page');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }

    onBexioConnect(): void {
        this.navigateToCompanyIntegrations();
    }

    /** Navigate to this company's integrations page (Bexio connect + integration request both land here). */
    private navigateToCompanyIntegrations(): void {
        const companyId = this.activeAnnualReport?.generalInformation?.company?._id;
        if (companyId == null) {
            return;
        }
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([`${locale}/${Paths.HOME}/${Paths.COMPANIES}/${companyId}/${Paths.INTEGRATIONS}`]);
    }

    onBexioSyncData(): void {
        if (this.activeAnnualReport?.generalInformation?.balanceSheetDate == null) {
            return;
        }
        const bsDate = new Date(this.activeAnnualReport.generalInformation.balanceSheetDate);
        const autoMatched = this.bexioBusinessYears.find((y) => {
            const end = new Date(y.end);
            return end.getFullYear() === bsDate.getFullYear() && end.getMonth() === bsDate.getMonth();
        });
        if (autoMatched != null) {
            this.onBexioImportRequested(autoMatched.id);
        }
    }

    get bexioNoMatchingYear(): boolean {
        if (this.bexioConnectionStatusValue !== 'connected') return false;
        if (this.bexioBusinessYears.length === 0) return false;
        const bsDate = this.activeAnnualReport?.generalInformation?.balanceSheetDate;
        if (bsDate == null) return false;
        const d = new Date(bsDate);
        return !this.bexioBusinessYears.some((y) => {
            const end = new Date(y.end);
            return end.getFullYear() === d.getFullYear() && end.getMonth() === d.getMonth();
        });
    }

    onIntegrationRequestClicked(): void {
        this.navigateToCompanyIntegrations();
    }

    /**
     * When the previous-year column is sourced from a previous Aumico annual report
     * (isPreviousYearDataTakenFromPreviousBalanceAnnualReport), accounts that had a
     * balance last year but ZERO movement this year are absent from the integration's
     * current-year accounts — the integration never returns previousYearAccounts in
     * this configuration. Re-add them so their prior-year value stays visible in the
     * previous-year column instead of silently vanishing.
     *
     * This is the integration-side equivalent of getExtraAccountsFromPreviousYear() in
     * the Excel import helper (financial-statement-import-form.helper.ts). Bexio and the
     * journal import (Abacus / D365) both call this so all three current-year sources
     * behave identically.
     */
    private buildPreviousYearOnlyAccountsFromReport(currentAccounts: IAccount[]): IAccount[] {
        const currentAccountNos = new Set(currentAccounts.map((a) => a.accountNumber));
        const extras: IAccount[] = [];
        const addMissing = (prevAccounts: IAccount[], type: AccountType, valueOf: (a: IAccount) => number): void => {
            for (const prev of prevAccounts) {
                if (currentAccountNos.has(prev.accountNumber)) {
                    continue;
                }
                extras.push({
                    accountNumber: prev.accountNumber,
                    name: prev.name,
                    rowNo: prev.rowNo,
                    type,
                    openingBalance: valueOf(prev),
                    closingBalance: 0,
                    shareholder: prev.shareholder,
                    comingFromPreviousAnnualReport: true,
                });
            }
        };
        addMissing(this.previousBalanceAccounts, AccountType.BALANCE, (a) => a.calculatedClosingBalance ?? a.closingBalance);
        addMissing(this.previousIncomeAccounts, AccountType.INCOME, (a) => a.calculatedClosingBalance ?? a.closingBalance);
        addMissing(
            this.previousBalanceSwitchAccounts,
            AccountType.BALANCE,
            // Reconstruct from the split legs first (immune to the stale, cross-year-propagated
            // originalClosingBalance — REV-21304 3rd-year carry); fall back to the summary otherwise.
            (a) =>
                this.switchClosingFromLegs(a as IBalanceSwitchAccount) ??
                (a as IBalanceSwitchAccount).originalClosingBalance ??
                a.closingBalance,
        );
        return extras;
    }

    onBexioImportSucceeded(result: BexioImportResult): void {
        // Bexio accountType mapping:
        //   3 = Assets (Aktiven)    → BALANCE
        //   4 = Liabilities/Equity  → BALANCE
        //   1 = Revenue             → INCOME
        //   2 = Expenses            → INCOME
        //   5 = Opening balance (Eröffnungsbilanz) → skip, not a real account

        // Build a lookup map from previous year closing balances → used as openingBalance.
        // previousYearAccounts is present only when isPreviousDataTakenFromPreviousAnnualReport
        // was false AND noPreviousYearData was false (i.e. we requested the previous year from Bexio).
        const prevBalanceMap = new Map<string, number>();
        if (result.previousYearAccounts != null) {
            for (const prev of result.previousYearAccounts) {
                prevBalanceMap.set(prev.accountNo, prev.saldo);
            }
        } else if (this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) {
            // Previous year data comes from another Aumico annual report — Bexio does not supply it.
            // Mirror what the Excel import helper does: use calculatedClosingBalance (already sign-adjusted
            // for that year's interpretation) so the engine does NOT apply a sign again. We mark these
            // accounts with comingFromPreviousAnnualReport=true which tells the engine to skip its sign
            // multiplication and use openingBalance verbatim — preventing a double-sign-flip if the previous
            // year was imported with a different Excel interpretation.
            for (const prev of [...this.previousBalanceAccounts, ...this.previousIncomeAccounts]) {
                prevBalanceMap.set(prev.accountNumber, prev.calculatedClosingBalance ?? prev.closingBalance);
            }
            // Switch accounts are stored separately in templateAndAccountsMapping.balanceSwitchAccounts
            // and are NOT returned by getAccountsOfTemplate() (which only traverses FLI account trees).
            // Without this, their openingBalance would be 0 instead of the previous year's closing value.
            // Use originalClosingBalance (raw Bexio-convention signed value) with closingBalance as fallback
            // for older switch accounts that were saved before originalClosingBalance was introduced.
            for (const switchAcc of this.previousBalanceSwitchAccounts) {
                prevBalanceMap.set(switchAcc.accountNumber, this.reliableSwitchPreviousClosing(switchAcc));
            }
        }

        const accounts: IAccount[] = result.accounts
            .filter((a) => a.accountType !== 5)
            .map((a) => {
                const openingBalance = prevBalanceMap.get(a.accountNo) ?? 0;
                // When openingBalance was sourced from a previous Aumico AR (calculatedClosingBalance),
                // the value is already sign-adjusted. We tell the engine not to apply the sign again.
                const comingFromPreviousAnnualReport =
                    this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport && prevBalanceMap.has(a.accountNo);
                return {
                    accountNumber: a.accountNo,
                    name: a.accountName,
                    type: a.accountType === 3 || a.accountType === 4 ? AccountType.BALANCE : AccountType.INCOME,
                    openingBalance,
                    closingBalance: a.saldo,
                    ...(comingFromPreviousAnnualReport ? { comingFromPreviousAnnualReport: true } : {}),
                };
            });

        // Accounts that had activity in the previous year but zero activity in the current year
        // are absent from result.accounts — add them with closingBalance=0 so they appear in the
        // mapping view with their prior-year value visible as openingBalance.
        if (result.previousYearAccounts != null) {
            const currentAccountNos = new Set(accounts.map((a) => a.accountNumber));
            for (const prev of result.previousYearAccounts.filter((p) => p.accountType !== 5)) {
                if (!currentAccountNos.has(prev.accountNo)) {
                    accounts.push({
                        accountNumber: prev.accountNo,
                        name: prev.accountName,
                        type: prev.accountType === 3 || prev.accountType === 4 ? AccountType.BALANCE : AccountType.INCOME,
                        openingBalance: prev.saldo,
                        closingBalance: 0,
                    });
                }
            }
        } else if (this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) {
            // Previous year came from a previous Aumico annual report, so Bexio never
            // returned previousYearAccounts — carry over prior-year-only accounts ourselves.
            accounts.push(...this.buildPreviousYearOnlyAccountsFromReport(accounts));
        }

        // Drop accounts where both closing and opening balance are zero — they are noise
        // with no prior-year or current-year value. Accounts that are zero this year but
        // had a balance last year (openingBalance ≠ 0) are intentionally kept so the
        // previous-year column is complete.
        const nonZeroAccounts = accounts.filter((a) => a.closingBalance !== 0 || a.openingBalance !== 0);
        this.onAccountsToMap(nonZeroAccounts);

        // Provide financialStatementSource entries with the Swiss KMU interpretation so that:
        // 1. getAnnualReportToBeSaved() does not bail out (guards on both keys being non-null).
        // 2. The mapping step's AccountMappingEngine receives a valid sheetSystem and can
        //    compute flip-signs correctly without any defensive patching elsewhere.
        // Bexio uses standard Swiss sign conventions: assets/liabilities/income positive, expenses negative.
        const bexioInterpretation = {
            numberFormat: NumberFormats.SINGLE,
            assets: Signs.POSITIVE,
            liabilities: Signs.POSITIVE,
            income: Signs.POSITIVE,
            expenses: Signs.NEGATIVE,
        };
        const bexioAccountsFilter = {
            accountNumber: '',
            accountName: '',
            openingValue: '',
            closingValue: '',
            excludedWord: '',
            includedWord: '',
            removeAccountWithZeroBalance: true,
        };
        this.financialStatementSource[FinancialStatementTypes.BALANCE] = {
            type: FinancialStatementTypes.BALANCE,
            interpretation: bexioInterpretation,
            accountsFilter: bexioAccountsFilter,
        } as IFinancialStatementSource;
        this.financialStatementSource[FinancialStatementTypes.INCOME] = {
            type: FinancialStatementTypes.INCOME,
            interpretation: bexioInterpretation,
            accountsFilter: bexioAccountsFilter,
        } as IFinancialStatementSource;

        // DO NOT call setEnforceOverwrite(true) here — that flag is only for DATEV/Excel
        // file imports and triggers the "Replace Taxonomy" dialog in the mapping step,
        // which is irrelevant for Bexio (there is no taxonomy involved).

        // Bexio is now the active data source — any pending Excel upload is superseded.
        this.excelFileUploaded = false;
        this.bexioImportedAt = new Date();

        // Unlock the "Continue to next step" button — same pattern as onImportFormCompletion.
        this.disableProceedButton = false;
        if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
            this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
        }
    }

    onBexioSummaryContinue(): void {
        this.showBexioSummaryModal = false;
        this.bexioProvider.resetImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], { relativeTo: this.activatedRoute });
    }

    onBexioSummaryDiscard(): void {
        this.showBexioSummaryModal = false;
        this.bexioSummaryResult = null;
        this.bexioSummaryCompanyName = null;
        this.bexioSyncedPreviousYearId = null;
        this.bexioProvider.resetImport();

        // Restore the previously saved accounts and sources (if any) instead of wiping them.
        // This way, if the user had already synced and saved before, the buttons stay enabled.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome  = incomeIndex  !== -1 ? this.activeFinStatements[incomeIndex]  : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts  = savedIncome.templateAndAccountsMapping.accounts  ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME]  = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    get bexioSummaryPreviousYear(): BexioBusinessYear | null {
        if (this.bexioSyncedPreviousYearId == null) return null;
        return this.bexioBusinessYears.find((y) => y.id === this.bexioSyncedPreviousYearId) ?? null;
    }

    // ── D365 BC handlers (mirror Bexio Pattern 1:1) ──────────────────────────

    /**
     * Card "Verbinden" CTA for a per-org vendor (D365 BC). Unlike Bexio (per-company,
     * connected from the Company profile), BC is connected + managed in the
     * Organization Settings. So instead of navigating we open an info modal that
     * points the user there.
     */
    onD365bcConnect(): void {
        this.showD365bcOrgModal = true;
    }

    onCloseD365bcOrgModal(): void {
        this.showD365bcOrgModal = false;
    }

    onD365bcGoToOrgSettings(): void {
        this.showD365bcOrgModal = false;
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    /**
     * Card "Daten synchronisieren" CTA. Auto matches the fiscal year by
     * comparing endDate (year + month) to the AR's balanceSheetDate, then
     * dispatches the import. If no FY matches, the `d365bcNoMatchingYear`
     * getter renders a hint on the card and we stay put.
     */
    onD365bcSyncData(): void {
        if (this.activeAnnualReport?.generalInformation?.balanceSheetDate == null) return;
        const bsDate = new Date(this.activeAnnualReport.generalInformation.balanceSheetDate);
        const autoMatched = this.d365bcFiscalYears.find((y) => {
            const end = new Date(y.endDate);
            return end.getFullYear() === bsDate.getFullYear() && end.getMonth() === bsDate.getMonth();
        });
        if (autoMatched != null) {
            this.onD365bcImportRequested(autoMatched);
        }
    }

    /** Same previous year heuristic as Bexio: immediately preceding by endDate. */
    onD365bcImportRequested(fiscalYear: IFiscalYear): void {
        const companyId = this.activeAnnualReport?.generalInformation?.company?._id;
        if (companyId == null || this.d365bcMapping == null) return;

        let previousFy: IFiscalYear | null = null;
        const needsPreviousYear =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;
        if (needsPreviousYear && this.d365bcFiscalYears.length > 1) {
            const selectedStart = new Date(fiscalYear.startDate).getTime();
            const candidates = this.d365bcFiscalYears.filter(
                (y) => y !== fiscalYear && new Date(y.endDate).getTime() < selectedStart,
            );
            if (candidates.length > 0) {
                previousFy = candidates.reduce((latest, y) =>
                    new Date(y.endDate).getTime() > new Date(latest.endDate).getTime() ? y : latest,
                );
            }
        }
        this.d365bcSyncedPreviousFiscalYear = previousFy;

        this.integrationProvider.runD365BcImport({
            companyId,
            connectionId: this.d365bcMapping.connectionId,
            externalCompanyId: this.d365bcMapping.externalCompanyId,
            fiscalYearStart: fiscalYear.startDate,
            fiscalYearEnd: fiscalYear.endDate,
            ...(previousFy != null
                ? {
                      previousFiscalYearStart: previousFy.startDate,
                      previousFiscalYearEnd: previousFy.endDate,
                  }
                : {}),
        });
    }

    // ── Abacus card handlers ─────────────────────────────────────────────
    /** Same "connect lives in Organization Settings" affordance as D365 BC. */
    onAbacusConnect(): void {
        this.showAbacusOrgModal = true;
    }

    onCloseAbacusOrgModal(): void {
        this.showAbacusOrgModal = false;
    }

    onAbacusGoToOrgSettings(): void {
        this.showAbacusOrgModal = false;
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    /**
     * Card "Daten synchronisieren" CTA. Abacus has no fiscal-year discovery,
     * so we derive the period dates directly from the active Annual Report's
     * General Info. Previous-year pair is included ONLY when the AR doesn't
     * already auto fill prior-year balances from another report.
     */
    onAbacusSyncData(): void {
        const gi = this.activeAnnualReport?.generalInformation;
        const companyId = gi?.company?._id;
        if (companyId == null || this.abacusMapping == null || gi == null) return;

        // Period for the current year. Prefer the explicit Income Statement
        // window (start + end); fall back to balance-sheet date for the end.
        const fiscalYearStart = gi.incomeCurrentYearFromDate;
        const fiscalYearEnd = gi.incomeCurrentYearToDate ?? gi.balanceSheetDate;
        if (fiscalYearStart == null || fiscalYearEnd == null) return;

        const needsPreviousYear =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;
        const prevStart = gi.incomePreviousYearFromDate;
        const prevEnd = gi.incomePreviousYearToDate ?? gi.balancePreviousYearDate;

        this.abacusSummaryPeriod = { from: fiscalYearStart, to: fiscalYearEnd };
        if (needsPreviousYear && prevStart != null && prevEnd != null) {
            this.abacusSummaryPreviousPeriod = { from: prevStart, to: prevEnd };
        } else {
            this.abacusSummaryPreviousPeriod = null;
        }

        const body: IAbacusImportBody = {
            companyId,
            connectionId: this.abacusMapping.connectionId,
            externalCompanyId: this.abacusMapping.externalCompanyId,
            fiscalYearStart,
            fiscalYearEnd,
            ...(this.abacusSummaryPreviousPeriod != null
                ? {
                      previousFiscalYearStart: this.abacusSummaryPreviousPeriod.from,
                      previousFiscalYearEnd: this.abacusSummaryPreviousPeriod.to,
                  }
                : {}),
        };

        this.integrationProvider.runAbacusImport(body);
    }

    // ── Topal card handlers (mirror Abacus) ──────────────────────────────
    onTopalConnect(): void {
        this.showTopalOrgModal = true;
    }

    onCloseTopalOrgModal(): void {
        this.showTopalOrgModal = false;
    }

    onTopalGoToOrgSettings(): void {
        this.showTopalOrgModal = false;
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    /** Card "Daten synchronisieren" CTA. Dates come from General Info (no discovery). */
    onTopalSyncData(): void {
        const gi = this.activeAnnualReport?.generalInformation;
        const companyId = gi?.company?._id;
        if (companyId == null || this.topalMapping == null || gi == null) return;

        const fiscalYearStart = gi.incomeCurrentYearFromDate;
        const fiscalYearEnd = gi.incomeCurrentYearToDate ?? gi.balanceSheetDate;
        if (fiscalYearStart == null || fiscalYearEnd == null) return;

        const needsPreviousYear =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;
        const prevStart = gi.incomePreviousYearFromDate;
        const prevEnd = gi.incomePreviousYearToDate ?? gi.balancePreviousYearDate;

        this.topalSummaryPeriod = { from: fiscalYearStart, to: fiscalYearEnd };
        if (needsPreviousYear && prevStart != null && prevEnd != null) {
            this.topalSummaryPreviousPeriod = { from: prevStart, to: prevEnd };
        } else {
            this.topalSummaryPreviousPeriod = null;
        }

        const body: ITopalImportBody = {
            companyId,
            connectionId: this.topalMapping.connectionId,
            externalCompanyId: this.topalMapping.externalCompanyId,
            fiscalYearStart,
            fiscalYearEnd,
            ...(this.topalSummaryPreviousPeriod != null
                ? {
                      previousFiscalYearStart: this.topalSummaryPreviousPeriod.from,
                      previousFiscalYearEnd: this.topalSummaryPreviousPeriod.to,
                  }
                : {}),
        };

        this.integrationProvider.runTopalImport(body);
    }

    // ── Accounto card handlers (mirror Topal) ──────────────────────────────
    onAccountoConnect(): void {
        this.showAccountoOrgModal = true;
    }

    onCloseAccountoOrgModal(): void {
        this.showAccountoOrgModal = false;
    }

    onAccountoGoToOrgSettings(): void {
        this.showAccountoOrgModal = false;
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    /** Card "Daten synchronisieren" CTA. Dates come from General Info (no discovery). */
    onAccountoSyncData(): void {
        const gi = this.activeAnnualReport?.generalInformation;
        const companyId = gi?.company?._id;
        const annualReportId = this.activeAnnualReport?._id;
        if (companyId == null || annualReportId == null || this.accountoMapping == null || gi == null) return;

        const fiscalYearStart = gi.incomeCurrentYearFromDate;
        const fiscalYearEnd = gi.incomeCurrentYearToDate ?? gi.balanceSheetDate;
        if (fiscalYearStart == null || fiscalYearEnd == null) return;

        const needsPreviousYear =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;
        const prevStart = gi.incomePreviousYearFromDate;
        const prevEnd = gi.incomePreviousYearToDate ?? gi.balancePreviousYearDate;

        this.accountoSummaryPeriod = { from: fiscalYearStart, to: fiscalYearEnd };
        if (needsPreviousYear && prevStart != null && prevEnd != null) {
            this.accountoSummaryPreviousPeriod = { from: prevStart, to: prevEnd };
        } else {
            this.accountoSummaryPreviousPeriod = null;
        }

        const body: IAccountoImportBody = {
            companyId,
            connectionId: this.accountoMapping.connectionId,
            externalCompanyId: this.accountoMapping.externalCompanyId,
            fiscalYearStart,
            fiscalYearEnd,
            ...(this.accountoSummaryPreviousPeriod != null
                ? {
                      previousFiscalYearStart: this.accountoSummaryPreviousPeriod.from,
                      previousFiscalYearEnd: this.accountoSummaryPreviousPeriod.to,
                  }
                : {}),
        };

        this.integrationProvider.runAccountoImport(body, annualReportId);
    }

    /**
     * Leaving the step forgets the Proffix and Accounto runs: a result still in the store (summary left
     * open) is not replayed when an import step opens again, and a run still in flight lands without a
     * target, so no report takes it (`proffixImportResultForReport`, `accountoImportResultForReport`).
     */
    override ngOnDestroy(): void {
        this.integrationProvider.resetProffixImport();
        this.integrationProvider.resetAccountoImport();
        super.ngOnDestroy();
    }

    // ── Proffix card handlers (mirror Topal) ──────────────────────────────
    onProffixConnect(): void {
        this.showProffixOrgModal = true;
    }

    onCloseProffixOrgModal(): void {
        this.showProffixOrgModal = false;
    }

    onProffixGoToOrgSettings(): void {
        this.showProffixOrgModal = false;
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    /** Card "Daten synchronisieren" CTA. Dates come from General Info (no discovery). */
    onProffixSyncData(): void {
        const gi = this.activeAnnualReport?.generalInformation;
        const companyId = gi?.company?._id;
        const annualReportId = this.activeAnnualReport?._id;
        if (companyId == null || annualReportId == null || this.proffixMapping == null || gi == null) return;

        const fiscalYearStart = gi.incomeCurrentYearFromDate;
        const fiscalYearEnd = gi.incomeCurrentYearToDate ?? gi.balanceSheetDate;
        if (fiscalYearStart == null || fiscalYearEnd == null) return;

        const needsPreviousYear =
            !this.noPreviousYearData && !this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport;
        const prevStart = gi.incomePreviousYearFromDate;
        const prevEnd = gi.incomePreviousYearToDate ?? gi.balancePreviousYearDate;

        this.proffixSummaryPeriod = { from: fiscalYearStart, to: fiscalYearEnd };
        if (needsPreviousYear && prevStart != null && prevEnd != null) {
            this.proffixSummaryPreviousPeriod = { from: prevStart, to: prevEnd };
        } else {
            this.proffixSummaryPreviousPeriod = null;
        }

        const body: IProffixImportBody = {
            companyId,
            connectionId: this.proffixMapping.connectionId,
            externalCompanyId: this.proffixMapping.externalCompanyId,
            fiscalYearStart,
            fiscalYearEnd,
            ...(this.proffixSummaryPreviousPeriod != null
                ? {
                      previousFiscalYearStart: this.proffixSummaryPreviousPeriod.from,
                      previousFiscalYearEnd: this.proffixSummaryPreviousPeriod.to,
                  }
                : {}),
        };

        this.integrationProvider.runProffixImport(body, annualReportId);
    }

    // ── Integration card visibility ──────────────────────────────────────
    // If a Company already has an integration linked, the import step shows ONLY
    // that card. With nothing linked, the full catalog (Bexio + D365 BC + coming
    // soon) is shown so the user can pick one.
    /** Bexio counts as linked while connected or merely expired (needs reauth). */
    get bexioLinked(): boolean {
        return this.bexioConnectionStatusValue === 'connected' || this.bexioConnectionStatusValue === 'expired';
    }
    /** D365 BC is linked when this Company carries a D365_BC connection mapping. */
    get d365bcLinked(): boolean {
        return this.d365bcMapping != null;
    }
    /** Abacus is linked when this Company carries an ABACUS connection mapping. */
    get abacusLinked(): boolean {
        return this.abacusMapping != null;
    }
    /** Topal is linked when this Company carries a TOPAL connection mapping. */
    get topalLinked(): boolean {
        return this.topalMapping != null;
    }
    /** Accounto is linked when this Company carries an ACCOUNTO connection mapping. */
    get accountoLinked(): boolean {
        return this.accountoMapping != null;
    }
    /** Proffix is linked when this Company carries a PROFFIX connection mapping. */
    get proffixLinked(): boolean {
        return this.proffixMapping != null;
    }
    get anyIntegrationLinked(): boolean {
        return (
            this.bexioLinked ||
            this.d365bcLinked ||
            this.abacusLinked ||
            this.topalLinked ||
            this.proffixLinked ||
            this.accountoLinked
        );
    }
    get showBexioCard(): boolean {
        return !this.anyIntegrationLinked || this.bexioLinked;
    }
    get showD365bcCard(): boolean {
        return !this.anyIntegrationLinked || this.d365bcLinked;
    }
    get showAbacusCard(): boolean {
        return !this.anyIntegrationLinked || this.abacusLinked;
    }
    get showTopalCard(): boolean {
        return !this.anyIntegrationLinked || this.topalLinked;
    }
    get showAccountoCard(): boolean {
        return !this.anyIntegrationLinked || this.accountoLinked;
    }
    /** Last Accounto sync of this report. */
    get accountoLastSyncedAt(): Date | null {
        return this.activeAnnualReport?.integrations?.accounto?.lastSyncedAt ?? null;
    }
    get showProffixCard(): boolean {
        return !this.anyIntegrationLinked || this.proffixLinked;
    }
    /** Last Proffix sync of this report. */
    get proffixLastSyncedAt(): Date | null {
        return this.activeAnnualReport?.integrations?.proffix?.lastSyncedAt ?? null;
    }
    get showComingSoonCard(): boolean {
        return !this.anyIntegrationLinked;
    }

    /** Getter equivalent to `bexioNoMatchingYear` — drives the card hint. */
    get d365bcNoMatchingYear(): boolean {
        if (this.d365bcCardStatus !== 'connected') return false;
        if (this.d365bcFiscalYears.length === 0) return false;
        const bsDate = this.activeAnnualReport?.generalInformation?.balanceSheetDate;
        if (bsDate == null) return false;
        const d = new Date(bsDate);
        return !this.d365bcFiscalYears.some((y) => {
            const end = new Date(y.endDate);
            return end.getFullYear() === d.getFullYear() && end.getMonth() === d.getMonth();
        });
    }

    /**
     * Processes the D365 BC import response into Aumico's IAccount shape so the
     * existing mapping engine picks it up. The body is vendor-agnostic and
     * lives in `applyJournalImportResult`; this entry point only owns the
     * D365 BC specific `*ImportedAt` flag that gates the save-time patch.
     */
    onD365bcImportSucceeded(result: IJournalImportResult): void {
        this.applyJournalImportResult(result);
        this.d365bcUnmappableAccounts = this.collectUnmappableAccounts(result);
        this.d365bcImportedAt = new Date();
    }

    /**
     * Abacus import success: identical processing to D365 BC (shared
     * IJournalImportResult shape), just routes the imported-at flag to the
     * Abacus side. Once the BE type release adds `abacus` to
     * `IAnnualReport['integrations']`, the save flow will pick this up and
     * patch `integrations.abacus.lastSyncedAt`.
     */
    onAbacusImportSucceeded(result: IJournalImportResult): void {
        this.applyJournalImportResult(result);
        this.abacusUnmappableAccounts = this.collectUnmappableAccounts(result);
        this.abacusImportedAt = new Date();
    }

    /** Topal import success: identical shared processing, routes the imported-at flag to Topal. */
    onTopalImportSucceeded(result: IJournalImportResult): void {
        this.applyJournalImportResult(result);
        this.topalImportedAt = new Date();
    }

    /** Accounto import success: identical shared processing, routes the imported-at flag to Accounto. */
    onAccountoImportSucceeded(result: IJournalImportResult): void {
        this.applyJournalImportResult(result);
        this.accountoImportedAt = new Date();
    }

    /** Proffix import success: identical shared processing, routes the imported-at flag to Proffix. */
    onProffixImportSucceeded(result: IJournalImportResult): void {
        this.applyJournalImportResult(result);
        this.proffixImportedAt = new Date();
    }

    /**
     * Accounts the vendor could not assign to a statement, i.e. the ones `applyJournalImportResult`
     * drops because their category is empty. Their saldo is therefore missing from the report,
     * which is why the summary modal names them and tells the user to categorise them at source.
     *
     * Only accounts that carry a VALUE are reported. A zero-balance account is dropped by the
     * non-zero filter anyway, so categorising it would change nothing — and D365 BC's technical
     * accounts (9000 Erfolgsrechnung, 9100 Eröffnungsbilanz) arrive uncategorised and empty on
     * every tenant, so including them would make the warning appear on every single import.
     *
     * Both years are considered: an account with no movement this year but a prior-year balance
     * still contributes an opening balance, so losing it loses data too.
     */
    private collectUnmappableAccounts(result: IJournalImportResult): IUnmappableAccounts | null {
        const affected = new Set<string>();
        for (const account of [...result.accounts, ...(result.previousYearAccounts ?? [])]) {
            if (account.category === '' && account.saldo !== 0) {
                affected.add(account.accountNo);
            }
        }
        if (affected.size === 0) {
            return null;
        }

        const listed = [...affected].slice(0, UNMAPPABLE_ACCOUNTS_DISPLAY_LIMIT).join(', ');
        return {
            count: affected.size,
            accountNumbers: affected.size > UNMAPPABLE_ACCOUNTS_DISPLAY_LIMIT ? `${listed}, …` : listed,
        };
    }

    /**
     * Category → AccountType mapping:
     *   Assets, Liabilities, Equity   → BALANCE
     *   Income, CostOfGoodsSold, Expense → INCOME
     *   ''                            → skip (opening balance carrier)
     *
     * Signs: BE doc states `saldo` is sign-corrected for display. We use the
     * same interpretation as Bexio (assets/liabilities/income positive,
     * expenses negative) so the mapping engine flip-sign logic stays uniform.
     */
    private applyJournalImportResult(result: IJournalImportResult): void {
        const isBalanceCategory = (c: IJournalImportResult['accounts'][number]['category']): boolean =>
            c === 'Assets' || c === 'Liabilities' || c === 'Equity';
        const isIncomeCategory = (c: IJournalImportResult['accounts'][number]['category']): boolean =>
            c === 'Income' || c === 'CostOfGoodsSold' || c === 'Expense';

        // Build previous year lookup, same approach as Bexio's onBexioImportSucceeded.
        const prevBalanceMap = new Map<string, number>();
        if (result.previousYearAccounts != null) {
            for (const prev of result.previousYearAccounts) {
                prevBalanceMap.set(prev.accountNo, prev.saldo);
            }
        } else if (this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) {
            for (const prev of [...this.previousBalanceAccounts, ...this.previousIncomeAccounts]) {
                prevBalanceMap.set(
                    prev.accountNumber,
                    prev.calculatedClosingBalance ?? prev.closingBalance,
                );
            }
            for (const switchAcc of this.previousBalanceSwitchAccounts) {
                prevBalanceMap.set(
                    switchAcc.accountNumber,
                    this.reliableSwitchPreviousClosing(switchAcc),
                );
            }
        }

        const accounts: IAccount[] = result.accounts
            .filter((a) => isBalanceCategory(a.category) || isIncomeCategory(a.category))
            .map((a) => {
                const openingBalance = prevBalanceMap.get(a.accountNo) ?? 0;
                const comingFromPreviousAnnualReport =
                    this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport &&
                    prevBalanceMap.has(a.accountNo);
                return {
                    accountNumber: a.accountNo,
                    name: a.accountName,
                    type: isBalanceCategory(a.category) ? AccountType.BALANCE : AccountType.INCOME,
                    openingBalance,
                    closingBalance: a.saldo,
                    ...(comingFromPreviousAnnualReport
                        ? { comingFromPreviousAnnualReport: true }
                        : {}),
                };
            });

        // Carry over previous-year-only accounts (had activity then, zero now)
        // so the mapping view shows their prior-year value.
        if (result.previousYearAccounts != null) {
            const currentAccountNos = new Set(accounts.map((a) => a.accountNumber));
            for (const prev of result.previousYearAccounts) {
                if (!isBalanceCategory(prev.category) && !isIncomeCategory(prev.category)) continue;
                if (!currentAccountNos.has(prev.accountNo)) {
                    accounts.push({
                        accountNumber: prev.accountNo,
                        name: prev.accountName,
                        type: isBalanceCategory(prev.category)
                            ? AccountType.BALANCE
                            : AccountType.INCOME,
                        openingBalance: prev.saldo,
                        closingBalance: 0,
                    });
                }
            }
        } else if (this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) {
            // Previous year came from a previous Aumico annual report, so the journal
            // import never returned previousYearAccounts — carry over prior-year-only
            // accounts ourselves.
            accounts.push(...this.buildPreviousYearOnlyAccountsFromReport(accounts));
        }

        // Drop pure-zero accounts (same rule as Bexio).
        const nonZeroAccounts = accounts.filter(
            (a) => a.closingBalance !== 0 || a.openingBalance !== 0,
        );
        this.onAccountsToMap(nonZeroAccounts);

        const d365bcInterpretation = {
            numberFormat: NumberFormats.SINGLE,
            assets: Signs.POSITIVE,
            liabilities: Signs.POSITIVE,
            income: Signs.POSITIVE,
            expenses: Signs.NEGATIVE,
        };
        const d365bcAccountsFilter = {
            accountNumber: '',
            accountName: '',
            openingValue: '',
            closingValue: '',
            excludedWord: '',
            includedWord: '',
            removeAccountWithZeroBalance: true,
        };
        this.financialStatementSource[FinancialStatementTypes.BALANCE] = {
            type: FinancialStatementTypes.BALANCE,
            interpretation: d365bcInterpretation,
            accountsFilter: d365bcAccountsFilter,
        } as IFinancialStatementSource;
        this.financialStatementSource[FinancialStatementTypes.INCOME] = {
            type: FinancialStatementTypes.INCOME,
            interpretation: d365bcInterpretation,
            accountsFilter: d365bcAccountsFilter,
        } as IFinancialStatementSource;

        this.excelFileUploaded = false;

        this.disableProceedButton = false;
        if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
            this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
        }
    }

    onD365BcSummaryContinue(): void {
        this.showD365BcSummaryModal = false;
        this.integrationProvider.resetD365BcImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], {
            relativeTo: this.activatedRoute,
        });
    }

    onAbacusSummaryContinue(): void {
        this.showAbacusSummaryModal = false;
        this.integrationProvider.resetAbacusImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], {
            relativeTo: this.activatedRoute,
        });
    }

    onAbacusSummaryDiscard(): void {
        this.showAbacusSummaryModal = false;
        this.abacusSummaryResult = null;
        this.abacusSummaryCompanyName = null;
        this.abacusSummaryPeriod = null;
        this.abacusSummaryPreviousPeriod = null;
        this.abacusUnmappableAccounts = null;
        this.integrationProvider.resetAbacusImport();
        this.abacusImportedAt = null;

        // Same recovery as D365 BC: restore previously saved accounts if any.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome = incomeIndex !== -1 ? this.activeFinStatements[incomeIndex] : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts = savedIncome.templateAndAccountsMapping.accounts ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    onTopalSummaryContinue(): void {
        this.showTopalSummaryModal = false;
        this.integrationProvider.resetTopalImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], {
            relativeTo: this.activatedRoute,
        });
    }

    onTopalSummaryDiscard(): void {
        this.showTopalSummaryModal = false;
        this.topalSummaryResult = null;
        this.topalSummaryCompanyName = null;
        this.topalSummaryPeriod = null;
        this.topalSummaryPreviousPeriod = null;
        this.integrationProvider.resetTopalImport();
        this.topalImportedAt = null;

        // Same recovery as Abacus/D365 BC: restore previously saved accounts if any.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome = incomeIndex !== -1 ? this.activeFinStatements[incomeIndex] : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts = savedIncome.templateAndAccountsMapping.accounts ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    onAccountoSummaryContinue(): void {
        this.showAccountoSummaryModal = false;
        this.integrationProvider.resetAccountoImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], {
            relativeTo: this.activatedRoute,
        });
    }

    onAccountoSummaryDiscard(): void {
        this.showAccountoSummaryModal = false;
        this.accountoSummaryResult = null;
        this.accountoSummaryCompanyName = null;
        this.accountoSummaryPeriod = null;
        this.accountoSummaryPreviousPeriod = null;
        this.integrationProvider.resetAccountoImport();
        this.accountoImportedAt = null;

        // Same recovery as Topal/Abacus/D365 BC: restore previously saved accounts if any.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome = incomeIndex !== -1 ? this.activeFinStatements[incomeIndex] : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts = savedIncome.templateAndAccountsMapping.accounts ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    onProffixSummaryContinue(): void {
        this.showProffixSummaryModal = false;
        this.integrationProvider.resetProffixImport();
        this.save();
        void this.router.navigate(['../', this.mappingStepPath], {
            relativeTo: this.activatedRoute,
        });
    }

    onProffixSummaryDiscard(): void {
        this.showProffixSummaryModal = false;
        this.proffixSummaryResult = null;
        this.proffixSummaryCompanyName = null;
        this.proffixSummaryPeriod = null;
        this.proffixSummaryPreviousPeriod = null;
        this.integrationProvider.resetProffixImport();
        this.proffixImportedAt = null;

        // Same recovery as Topal/Abacus/D365 BC: restore previously saved accounts if any.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome = incomeIndex !== -1 ? this.activeFinStatements[incomeIndex] : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts = savedIncome.templateAndAccountsMapping.accounts ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    onD365BcSummaryDiscard(): void {
        this.showD365BcSummaryModal = false;
        this.d365bcSummaryResult = null;
        this.d365bcSummaryCompanyName = null;
        this.d365bcSyncedPreviousFiscalYear = null;
        this.d365bcUnmappableAccounts = null;
        this.integrationProvider.resetD365BcImport();

        // Same recovery logic as Bexio: restore previously saved accounts if any.
        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            this.activeFinStatements,
            FinancialStatementTypes.INCOME,
        );
        const savedBalance = balanceIndex !== -1 ? this.activeFinStatements[balanceIndex] : null;
        const savedIncome = incomeIndex !== -1 ? this.activeFinStatements[incomeIndex] : null;

        if (savedBalance != null && savedIncome != null) {
            this.balanceAccounts = savedBalance.templateAndAccountsMapping.accounts ?? [];
            this.incomeAccounts = savedIncome.templateAndAccountsMapping.accounts ?? [];
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep(savedBalance.import);
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep(savedIncome.import);
            const hasAccounts = this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0;
            this.disableProceedButton = !hasAccounts;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !hasAccounts;
            }
        } else {
            this.balanceAccounts = [];
            this.incomeAccounts = [];
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            this.disableProceedButton = true;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = true;
            }
        }
    }

    onExtraExcelContentChanged(extraContent: IExtraExcelContent | null) {
        this.extraExcelContent = extraContent;
        debug('Extra Excel content updated:', extraContent);
    }

    private handleGetActiveAnnualReport(annualReport: IAnnualReport, finStatements: IFinStatements[]): void {
        if (annualReport == null) {
            return;
        }

        const companyId = annualReport.generalInformation?.company?._id;
        if (companyId != null) {
            this.bexioProvider.loadConnection(companyId);

            // D365 BC: the AR endpoint slim projects the company without
            // `integrations`, so we re fetch the full company via the same
            // companies service Bexio uses. The full `ICompanyDTO` contains
            // `integrations.connectionMappings` inline (per BE handoff).
            this.companiesService
                .getCompanyById(companyId)
                .pipe(untilDestroyed(this))
                .subscribe((fullCompany) => {
                    const mappings =
                        (fullCompany as unknown as {
                            integrations?: {
                                connectionMappings?: Array<{
                                    vendor: string;
                                    connectionId: string;
                                    externalCompanyId: string;
                                }>;
                            };
                        })?.integrations?.connectionMappings ?? [];
                    const d365Mapping = mappings.find((m) => m.vendor === 'D365_BC');
                    if (d365Mapping != null) {
                        this.d365bcMapping = {
                            connectionId: d365Mapping.connectionId,
                            externalCompanyId: d365Mapping.externalCompanyId,
                        };
                        this.d365bcCardStatus = 'connected';
                        this.integrationProvider.loadD365BcFiscalYears(
                            d365Mapping.connectionId,
                            d365Mapping.externalCompanyId,
                        );
                    } else {
                        this.d365bcMapping = null;
                        this.d365bcCardStatus = 'idle';
                    }
                    // Abacus has no fiscal-year preload step (no discovery API);
                    // the import handler reads the dates from General Info instead.
                    const abacusMapping = mappings.find((m) => m.vendor === 'ABACUS');
                    if (abacusMapping != null) {
                        this.abacusMapping = {
                            connectionId: abacusMapping.connectionId,
                            externalCompanyId: abacusMapping.externalCompanyId,
                        };
                        this.abacusCardStatus = 'connected';
                    } else {
                        this.abacusMapping = null;
                        this.abacusCardStatus = 'idle';
                    }
                    // Topal: same as Abacus — no fiscal-year preload (dates from General Info).
                    const topalMapping = mappings.find((m) => m.vendor === 'TOPAL');
                    if (topalMapping != null) {
                        this.topalMapping = {
                            connectionId: topalMapping.connectionId,
                            externalCompanyId: topalMapping.externalCompanyId,
                        };
                        this.topalCardStatus = 'connected';
                    } else {
                        this.topalMapping = null;
                        this.topalCardStatus = 'idle';
                    }
                    // Accounto: same as Topal — no fiscal-year preload (dates from General Info).
                    const accountoMapping = mappings.find((m) => m.vendor === 'ACCOUNTO');
                    if (accountoMapping != null) {
                        this.accountoMapping = {
                            connectionId: accountoMapping.connectionId,
                            externalCompanyId: accountoMapping.externalCompanyId,
                        };
                        this.accountoCardStatus = 'connected';
                    } else {
                        this.accountoMapping = null;
                        this.accountoCardStatus = 'idle';
                    }
                    // Proffix: same as Topal — no fiscal-year preload (dates from General Info).
                    const proffixMapping = mappings.find((m) => m.vendor === 'PROFFIX');
                    if (proffixMapping != null) {
                        this.proffixMapping = {
                            connectionId: proffixMapping.connectionId,
                            externalCompanyId: proffixMapping.externalCompanyId,
                        };
                        this.proffixCardStatus = 'connected';
                    } else {
                        this.proffixMapping = null;
                        this.proffixCardStatus = 'idle';
                    }
                });
        } else {
            this.d365bcMapping = null;
            this.d365bcCardStatus = 'idle';
            this.abacusMapping = null;
            this.abacusCardStatus = 'idle';
            this.topalMapping = null;
            this.topalCardStatus = 'idle';
            this.accountoMapping = null;
            this.accountoCardStatus = 'idle';
            this.proffixMapping = null;
            this.proffixCardStatus = 'idle';
        }

        if (annualReport.autoFillAnnualReportId != null) {
            this.annualReportProvider.setBalanceAutoFillIdWithChosenAnnualReportId(annualReport.autoFillAnnualReportId);

            this.annualReportProvider.dispatchBalanceAutoFillFromChosenOrSavedId(annualReport.autoFillAnnualReportId);
        }

        if (annualReport.autoFillAnnualReportIdForIncomeStatement != null) {
            this.annualReportProvider.setIncomeAutoFillIdWithChosenAnnualReportId(
                annualReport.autoFillAnnualReportIdForIncomeStatement,
            );

            this.annualReportProvider.dispatchIncomeAutoFillFromChosenOrSavedId(
                annualReport.autoFillAnnualReportIdForIncomeStatement,
            );
        } else if (annualReport.autoFillAnnualReportId != null) {
            // For old annual reports that do not have a separate id for income statement autofill,
            this.annualReportProvider.setIncomeAutoFillIdWithChosenAnnualReportId(annualReport.autoFillAnnualReportId);
            this.annualReportProvider.dispatchIncomeAutoFillFromChosenOrSavedId(annualReport.autoFillAnnualReportId);
        }

        const event: IAumGlobalActionEvent = {
            action: AumGlobalAction.UPDATECURRENTENTITY,
            value: annualReport.generalInformation?.company != null ? annualReport.generalInformation.company.name : '',
        };
        this.actionHeaderService.onAction(event);
        this.activeAnnualReport = cloneDeep<IAnnualReport>(annualReport);
        this.activeFinStatements = cloneDeep<IFinStatements[]>(finStatements);

        this.defineSettings();

        this.selectedAccountingBasisId = this.activeAnnualReport.generalInformation?.accountingBasis._id;
        this.selectedCompanyLegalFormId = this.activeAnnualReport.generalInformation?.company.legalform._id;
        this.reportLocale = this.activeAnnualReport.locale.toLowerCase();

        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            finStatements,
            FinancialStatementTypes.BALANCE,
        );
        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            finStatements,
            FinancialStatementTypes.INCOME,
        );

        if (this.activeAnnualReport.templateReportAutofillOptions?.[TAXONAMY] != null) {
            this.uploadAndMappingAutoFillAnnualReportIdExist = true;
        }

        if (finStatements[balanceIndex] != null) {
            this.financialStatementSource[FinancialStatementTypes.BALANCE] = cloneDeep<IFinancialStatementSource>(
                finStatements[balanceIndex].import,
            );
            this.balanceAccounts = finStatements[balanceIndex].templateAndAccountsMapping.accounts;
            if (finStatements[balanceIndex].templateAndAccountsMapping.template != null) {
                // if mapping has been previously saved, then we need to disable the 'take autofill & mapping from selected annual report' toggle in the import options
                this.uploadAndMappingAutoFillAnnualReportToggleDisabled = true;
            }
        }
        if (finStatements[incomeIndex] != null) {
            this.financialStatementSource[FinancialStatementTypes.INCOME] = cloneDeep<IFinancialStatementSource>(
                finStatements[incomeIndex].import,
            );
            this.incomeAccounts = finStatements[incomeIndex].templateAndAccountsMapping.accounts;
            if (finStatements[incomeIndex].templateAndAccountsMapping.template != null) {
                // if mapping has been previously saved, then we need to disable the 'take autofill & mapping from selected annual report' toggle in the import options
                this.uploadAndMappingAutoFillAnnualReportToggleDisabled = true;
            }
        }

        if (annualReport.generalInformation != null && Object.keys(annualReport.generalInformation).length !== 0) {
            this.noPreviousYearData = annualReport.generalInformation.noPreviousData;
            this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport =
                annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReport;
            this.isPreviousYearDataTakenFromPreviousIncomeAnnualReport =
                annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement;
            if (
                annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReport ||
                annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement
            ) {
                this.uploadAndMappingAutoFillAnnualReportToggleDisabled = true;
            }
        }

        // If accounts are already saved (e.g. returning from mapping step), unlock the proceed button.
        if (this.balanceAccounts.length > 0 && this.incomeAccounts.length > 0) {
            this.disableProceedButton = false;
            if (this.footerExtraButtonActions?.[FIRST_ELEMENT_INDEX] != null) {
                this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;
            }
        }
    }

    private defineSettings(): void {
        if (
            this.activeAnnualReport.annualReportTemplate != null &&
            this.activeAnnualReport.annualReportTemplate.settings != null &&
            this.activeAnnualReport.annualReportTemplate.settings.showShareholderInfo != null
        ) {
            this.showShareholderInformation = this.activeAnnualReport.annualReportTemplate.settings.showShareholderInfo;
        } else {
            this.showShareholderInformation = false;
        }
    }

    private assignBalanceAndIncomeSources(autofillFinStatements: IFinStatements[]): void {
        if (autofillFinStatements == null || autofillFinStatements.length === 0) {
            this.financialStatementSource = {} as FinancialStatementSourceMap;
            return;
        }
        autofillFinStatements.forEach((finStatement) => {
            if (finStatement && finStatement.type === FinancialStatementTypes.BALANCE && finStatement.import) {
                this.financialStatementSource = {
                    ...this.financialStatementSource,
                    [FinancialStatementTypes.BALANCE]: finStatement.import,
                };
            } else if (finStatement && finStatement.type === FinancialStatementTypes.INCOME && finStatement.import) {
                this.financialStatementSource = {
                    ...this.financialStatementSource,
                    [FinancialStatementTypes.INCOME]: finStatement.import,
                };
            }
        });
    }

    private assignPreviousAccounts(autofillFinStatements: IFinStatements[]): void {
        if (autofillFinStatements == null || autofillFinStatements.length === 0) {
            return;
        }

        if (autofillFinStatements[templateConstants.balanceTemplate]) {
            this.previousBalanceAccounts = this.accountMapper.getAccountsOfTemplate(
                autofillFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template,
            );

            this.previousBalanceSwitchAccounts =
                autofillFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping
                    .balanceSwitchAccounts != null
                    ? autofillFinStatements[templateConstants.balanceTemplate].templateAndAccountsMapping
                          .balanceSwitchAccounts
                    : [];
        }

        if (autofillFinStatements[templateConstants.incomeTemplate]) {
            this.previousIncomeAccounts = this.accountMapper.getAccountsOfTemplate(
                autofillFinStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template,
            );
        }
    }

    onBalanceFinancialStatementSourceChange(financialStatementSource: IFinancialStatementSource): void {
        financialStatementSource.type = FinancialStatementTypes.BALANCE;
        this.financialStatementSource[financialStatementSource.type] = financialStatementSource;
    }

    onIncomeFinancialStatementSourceChange(financialStatementSource: IFinancialStatementSource): void {
        financialStatementSource.type = FinancialStatementTypes.INCOME;
        this.financialStatementSource[financialStatementSource.type] = financialStatementSource;
    }

    onImportedTaxonomyConstructed(importedTaxonomy: TaxonomyDefinitions): void {
        this.importedTaxonomy = importedTaxonomy;
        // REV-18627(C1d/R6): the signal is the delivered taxonomy itself — an accounts-only
        // file (B5) constructs a null taxonomy and must not trigger the dialog.
        this.setEnforceOverwrite(importedTaxonomy != null);
        // No AI panel needed for valid files (no errors)
    }

    // ── AI Fixer Methods ──────────────────────────────────────────────

    /**
     * Resets every piece of AI state on this component so the UI is clean again.
     * Used when the user removes the uploaded Excel (trash icon) or replaces it
     * — both invalidate any in-flight or completed AI fix.
     *
     * Also dispatches `resetSession()` on the NgRx store; the panel listens to
     * the status selector and, on a null status, returns itself to IDLE.
     * The backend job (if still running) will time out on its own via the 180s
     * outer timeout in the controller — we don't need to actively cancel it.
     */
    private resetAiState(): void {
        this.aiAgentProvider.resetSession();
        this.aiJobRequest = null;
        this.aiOriginalSections = null;
        this.aiOriginalSpreadSheet = null;
        this.aiFixedSections = null;
        this.aiAcceptedTaxonomy = null;
        this.showAiPanel = false;
        this.rebuildAiDelegate();
    }

    /**
     * Called when the form page detects a DATEV file with structural errors.
     * Builds the AI job request from the raw spreadsheet using the exact same
     * parsing logic as the onboarding tool's hierarchy-classifier component.
     */
    onDatevSpreadSheetWithErrors(spreadSheet: ISpreadSheet): void {
        // If the user is replacing an existing file while a previous AI session
        // was running / completed, wipe that stale state first so the new file
        // starts cleanly. Without this the panel could stay stuck on RUNNING /
        // COMPLETED for the old file while pointing at the new jobRequest.
        this.resetAiState();

        this.aiJobRequest = this.datevAiAdapter.buildJobRequestFromSpreadSheet(
            spreadSheet,
            this.activeAnnualReportEncryptedId,
        );
        // Store original sections for diff highlighting in the result view
        this.aiOriginalSections = this.datevAiAdapter.extractFixedSections(this.aiJobRequest.payload);
        // Keep the full spreadsheet so we can recover SpecialFLIs on accept
        this.aiOriginalSpreadSheet = spreadSheet;
        this.aiFixedSections = null;
        this.showAiPanel = true;

        // Fetch Firebase ID token for WebSocket authentication
        const currentUser = getAuth().currentUser;
        if (currentUser) {
            currentUser.getIdToken().then((token) => {
                this.firebaseToken = token;
                this.rebuildAiDelegate();
            });
        }

        this.rebuildAiDelegate();
    }

    /**
     * Called when the user accepts the AI-fixed result.
     *
     * Converts the fixed AumicoSections back into a TaxonomyDefinitions and
     * feeds it into the same pipeline as a successful direct DATEV import:
     *   1. Build TaxonomyDefinitions from fixed sections + original spreadsheet
     *   2. Set this.importedTaxonomy (used by prepareFinancialStatementsForSaving)
     *   3. Enable the proceed button and hide the AI panel
     */
    onAiAccepted(output: Record<string, unknown>): void {
        const fixedSections = this.datevAiAdapter.extractFixedSections(output);
        this.aiFixedSections = fixedSections;

        if (!this.aiOriginalSpreadSheet) {
            debug('AI accept: no original spreadsheet stored — cannot rebuild taxonomy');
            this.showAiPanel = false;
            this.rebuildAiDelegate();
            return;
        }

        const taxonomy = this.datevAiAdapter.buildTaxonomyFromFixedSections(this.aiOriginalSpreadSheet, fixedSections);

        if (!taxonomy) {
            debug('AI accept: buildTaxonomyFromFixedSections returned null (missing template-info sheet)');
            this.showAiPanel = false;
            this.rebuildAiDelegate();
            return;
        }

        // Treat as a successful import: store taxonomy and enable proceed
        this.importedTaxonomy = taxonomy;
        this.aiAcceptedTaxonomy = taxonomy;
        // REV-18627(C1d/R6, Review E1): same signal as onImportedTaxonomyConstructed — the AI fix
        // produced a taxonomy, so a follow-up report (also a shell) gets the replace dialog; the
        // user still decides in the dialog. Confirmed by Samuel Schmid, 2026-09-11 (review round 2).
        this.setEnforceOverwrite(true);
        this.disableProceedButton = false;

        this.showAiPanel = false;
        this.rebuildAiDelegate();
        debug('AI fix accepted and taxonomy rebuilt. Sections:', fixedSections);
    }

    /**
     * Called when the user rejects the AI fix or cancels.
     */
    onAiRejected(): void {
        this.aiFixedSections = null;
        this.showAiPanel = false;
        this.rebuildAiDelegate();
    }

    /**
     * Creates a fresh delegate object so Angular change detection picks up any
     * AI-state mutations via a new object reference on the [aiDelegate] input.
     */
    private rebuildAiDelegate(): void {
        if (!this.showAiPanel && !this.aiAcceptedTaxonomy) {
            this.aiDelegate = null;
            return;
        }
        this.aiDelegate = {
            showPanel: this.showAiPanel,
            jobRequest: this.aiJobRequest,
            firebaseToken: this.firebaseToken,
            originalSections: this.aiOriginalSections,
            fixedSections: this.aiFixedSections,
            fixedTaxonomy: this.aiAcceptedTaxonomy,
            onAccepted: (output) => this.onAiAccepted(output),
            onRejected: () => this.onAiRejected(),
        };
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        // Closes the UPLOAD_SHEET_TIME_SPENT_TILL_COMPLETION timed event opened
        // in ngOnInit. Must run on EVERY save (manual + integration) or the
        // timer stays open and corrupts the next reported duration.
        this.mixpanelService.track(MIXPANEL_EVENTS.UPLOAD_SHEET_TIME_SPENT_TILL_COMPLETION);

        if (!this.validateImportForm()) {
            return EMPTY;
        }

        return this.persistAnnualReport();
    }

    /**
     * Pre-save validation for the manual Excel/DATEV column-mapping form.
     *
     * Only enforced while an Excel/DATEV file is actually being mapped
     * (`excelFileUploaded`). Integration syncs (Bexio / D365 BC / Abacus),
     * autofill and already-persisted data leave that flag false and are exempt
     * — their data never flows through this form, so the completion flags
     * (`balanceCompleted` / `incomeStatementCompleted` / …) are meaningless for
     * them.
     *
     * `excelFileUploaded` is the DURABLE discriminator on purpose. The earlier
     * fix keyed off the per-vendor `*ImportedAt` flags, but those are cleared
     * inside `getAnnualReportToBeSaved()` — so by the time the CanDeactivate
     * route guard re-invokes `save()` (the integration handler navigates right
     * after the first save), the flags are already gone and the guard would
     * wrongly re-fire `aumico_reports_required_data_not_completed` and abort
     * navigation. `excelFileUploaded` only flips back to false on a successful
     * save (onSaveSuccessfully) or when an integration import supersedes the
     * upload, so it still reads false at guard time for sync data.
     *
     * Returns true when the save may proceed; otherwise fires the matching
     * error banner and returns false.
     */
    private validateImportForm(): boolean {
        // No manual Excel/DATEV mapping in progress → nothing to validate.
        if (!this.excelFileUploaded) {
            return true;
        }

        if (this.financialStatementImportFormPage === undefined) {
            debug(
                'Save aborted: financial statement import form reference not initialized. Ensure the form component is present in the template and has finished initialization.',
            );
            this.fireErrorMessage();
            return false;
        }

        this.financialStatementImportFormPage.flushPendingFormState();
        const validationState = this.financialStatementImportFormPage.getFormValidationState();
        const isImportFormValid =
            validationState.formControlsComplete &&
            validationState.incomeStatementCompleted &&
            validationState.balanceCompleted &&
            validationState.specialFlisFormCompleted &&
            validationState.accountFilterFormValid &&
            validationState.sameSheetSelectionComplete;
        if (!isImportFormValid) {
            debug(
                'Save aborted because the financial statement import form is invalid after flushing pending state.',
                validationState,
            );
            this.fireErrorMessage('aumico_reports_required_data_not_completed');
            return false;
        }

        return true;
    }

    /**
     * Persists the current annual-report state. Extracted so `save()` reads as
     * "validate, then persist".
     *
     * Ordering is load-bearing: `dispatchSaveAnnualReport` BEFORE
     * `enforceGlobalStateUpdate`, both inside the `isSaveRequestInProgress`
     * window — the REV-21193 autofill-overwrite guard depends on that flag
     * being true across the synchronous enforce phase.
     */
    private persistAnnualReport(): Observable<void> {
        this.isSaveRequestInProgress = true;
        const annualReportToBeSaved = this.getAnnualReportToBeSaved();
        this.dispatchSaveAnnualReport(annualReportToBeSaved);
        this.enforceGlobalStateUpdate(annualReportToBeSaved);
        return of(void 0);
    }

    getAnnualReportToBeSaved(): IAnnualReport | undefined {
        debug('save');
        if (
            this.activeAnnualReport == null ||
            this.financialStatementSource[TemplateType.BALANCE] == null ||
            this.financialStatementSource[TemplateType.INCOME] == null
        ) {
            return undefined;
        }

        // this.isAutoFillApplied = false;

        this.activeAnnualReport.templateReportAutofillOptions = this.uploadAutofillWithOptions;

        const isDataChanged = this.detectChangesInImportDefinition();

        const annualReportToBeSaved = this.prepareFinancialStatementsForSaving(
            this.activeAnnualReport,
            this.activeFinStatements,
            isDataChanged,
        );

        // Excel data is not from Bexio — mark integration as not the active source and clear the sync timestamp.
        if (this.excelFileUploaded) {
            annualReportToBeSaved.integrations = {
                ...annualReportToBeSaved.integrations,
                bexio: { isUsed: false, lastSyncedAt: null },
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations({
                bexio: { isUsed: false, lastSyncedAt: null },
            });
        }

        // Bexio import — persist the sync timestamp via the PUT body so the backend
        // writes it to the meta info document without going through aumico-be.
        if (this.bexioImportedAt != null) {
            const syncedAt = this.bexioImportedAt;
            this.bexioImportedAt = null;
            annualReportToBeSaved.integrations = {
                bexio: { isUsed: true, lastSyncedAt: syncedAt },
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations({
                bexio: { isUsed: true, lastSyncedAt: syncedAt },
            });
        }

        // D365 BC import — same pattern as Bexio. Requires `d365bc` on
        // `IAnnualReport['integrations']` in `@modeso/types__aumico-lib-annualreport-be`
        // (mirror of the `bexio` shape: `{ isUsed?: boolean; lastSyncedAt?: Date | null }`).
        // Typed on purpose: this fails to compile until that type release is installed.
        if (this.d365bcImportedAt != null) {
            const syncedAt = this.d365bcImportedAt;
            this.d365bcImportedAt = null;
            // `Pick<…, 'd365bc'>` on purpose: under this project's non-strict tsconfig a
            // missing property only yields `any` (no error), but a `keyof` constraint
            // violation is always reported (TS2344). So this fails to compile until the
            // type release adds `d365bc` to IAnnualReport['integrations'].
            const d365bcIntegrationPatch: Pick<NonNullable<IAnnualReport['integrations']>, 'd365bc'> = {
                d365bc: { isUsed: true, lastSyncedAt: syncedAt },
            };
            annualReportToBeSaved.integrations = {
                ...(annualReportToBeSaved.integrations ?? {}),
                ...d365bcIntegrationPatch,
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations(
                d365bcIntegrationPatch,
            );
        }

        // Abacus import — same pattern as D365 BC. Same `Pick<…, 'abacus'>` trick
        // so the FE fails to compile if the type release ever regresses.
        if (this.abacusImportedAt != null) {
            const syncedAt = this.abacusImportedAt;
            this.abacusImportedAt = null;
            const abacusIntegrationPatch: Pick<NonNullable<IAnnualReport['integrations']>, 'abacus'> = {
                abacus: { isUsed: true, lastSyncedAt: syncedAt },
            };
            annualReportToBeSaved.integrations = {
                ...(annualReportToBeSaved.integrations ?? {}),
                ...abacusIntegrationPatch,
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations(
                abacusIntegrationPatch,
            );
        }

        // Topal import — same `Pick<…, 'topal'>` trick (fails to compile if the type regresses).
        if (this.topalImportedAt != null) {
            const syncedAt = this.topalImportedAt;
            this.topalImportedAt = null;
            const topalIntegrationPatch: Pick<NonNullable<IAnnualReport['integrations']>, 'topal'> = {
                topal: { isUsed: true, lastSyncedAt: syncedAt },
            };
            annualReportToBeSaved.integrations = {
                ...(annualReportToBeSaved.integrations ?? {}),
                ...topalIntegrationPatch,
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations(
                topalIntegrationPatch,
            );
        }

        // Accounto import — same `Pick<…, 'accounto'>` trick (types 26.4.2-REV-22536.0 carry the key).
        if (this.accountoImportedAt != null) {
            const syncedAt = this.accountoImportedAt;
            this.accountoImportedAt = null;
            const accountoIntegrationPatch: Pick<NonNullable<IAnnualReport['integrations']>, 'accounto'> = {
                accounto: { isUsed: true, lastSyncedAt: syncedAt },
            };
            annualReportToBeSaved.integrations = {
                ...(annualReportToBeSaved.integrations ?? {}),
                ...accountoIntegrationPatch,
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations(
                accountoIntegrationPatch,
            );
        }

        // Proffix import — same `Pick<…, 'proffix'>` trick (types 26.4.2-REV-22536.0 carry the key).
        if (this.proffixImportedAt != null) {
            const syncedAt = this.proffixImportedAt;
            this.proffixImportedAt = null;
            const proffixIntegrationPatch: Pick<NonNullable<IAnnualReport['integrations']>, 'proffix'> = {
                proffix: { isUsed: true, lastSyncedAt: syncedAt },
            };
            annualReportToBeSaved.integrations = {
                ...(annualReportToBeSaved.integrations ?? {}),
                ...proffixIntegrationPatch,
            };
            this.annualReportProvider.dispatchPatchActiveAnnualReportIntegrations(
                proffixIntegrationPatch,
            );
        }

        this.annualReportProvider.setBalanceFinancialStatementSource(
            this.financialStatementSource[TemplateType.BALANCE],
        );
        this.annualReportProvider.setIncomeFinancialStatementSource(this.financialStatementSource[TemplateType.INCOME]);

        this.fileChanged = false;
        this.appStateDispatcher.setUploadedFileChanged(false);

        return annualReportToBeSaved;
    }

    private prepareFinancialStatementsForSaving(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        isDataChanged: boolean,
    ): IAnnualReport {
        const annualReportToBeSaved: IAnnualReport = cloneDeep<IAnnualReport>(annualReport);
        // It's required to replace annualReportToBeSaved.finStatements with the activeFinStatements
        // and then from here on we can modify the .finStatements of annualReportToBeSaved directly
        annualReportToBeSaved.finStatements = cloneDeep<IFinStatements[]>(finStatements);

        if (this.importedTaxonomy != null) {
            annualReportToBeSaved.importedTaxonomy = cloneDeep<TaxonomyDefinitions>(this.importedTaxonomy);
        }

        // Add extra Excel content for audit compliance (DATEV Sammelkonten/Kontokorrent preservation)
        if (this.extraExcelContent != null) {
            annualReportToBeSaved.extraExcelContent = cloneDeep<IExtraExcelContent>(this.extraExcelContent);
        } else {
            annualReportToBeSaved.extraExcelContent = null;
        }

        const balanceIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            finStatements,
            FinancialStatementTypes.BALANCE,
        );

        let { version } = annualReportToBeSaved.finStatements[balanceIndex] ?? { version: 0 };
        const updatedVersion = isDataChanged ? version + 1 : version;
        const balanceFinancialStatement: IFinStatements = {
            ...(balanceIndex != -1 ? annualReportToBeSaved.finStatements[balanceIndex] : ({} as any)),
            import: this.mapFinancialStatementSourceForSaving(
                this.financialStatementSource[FinancialStatementTypes.BALANCE],
            ),
            templateAndAccountsMapping: {
                accounts: this.balanceAccounts,
                balanceSwitchAccounts:
                    annualReportToBeSaved.finStatements.length !== EMPTY_ARRAY_LENGTH &&
                    annualReportToBeSaved.finStatements[balanceIndex]?.templateAndAccountsMapping != null &&
                    annualReportToBeSaved.finStatements[balanceIndex]?.templateAndAccountsMapping?.balanceSwitchAccounts
                        .length !== EMPTY_ARRAY_LENGTH
                        ? annualReportToBeSaved.finStatements[balanceIndex]?.templateAndAccountsMapping
                              ?.balanceSwitchAccounts
                        : [],
                template: this.resolveTemplate(finStatements, balanceIndex),
            },
            type: FinancialStatementTypes.BALANCE,
            version: updatedVersion,
        };
        if (balanceIndex === -1) {
            annualReportToBeSaved.finStatements.push(balanceFinancialStatement);
        } else {
            annualReportToBeSaved.finStatements[balanceIndex] = balanceFinancialStatement;
        }

        const incomeIndex = this.getIndexForFinancialStatementSourceFromActiveAnnualReport(
            finStatements,
            FinancialStatementTypes.INCOME,
        );
        const incomeFinancialStatement: IFinStatements = {
            ...(incomeIndex !== -1 ? annualReportToBeSaved.finStatements[incomeIndex] : ({} as any)),
            import: this.mapFinancialStatementSourceForSaving(
                this.financialStatementSource[FinancialStatementTypes.INCOME],
            ),
            templateAndAccountsMapping: {
                // if file is uploaded, then we take the prepared this.incomeAccounts coming from excel. Else, we take the saved accounts
                accounts: this.incomeAccounts,
                template: this.resolveTemplate(annualReportToBeSaved.finStatements, incomeIndex),
            },
            type: FinancialStatementTypes.INCOME,
            version: updatedVersion,
        };

        if (incomeIndex == -1) {
            annualReportToBeSaved.finStatements.push(incomeFinancialStatement);
        } else {
            annualReportToBeSaved.finStatements[incomeIndex] = incomeFinancialStatement;
        }

        return annualReportToBeSaved;
    }

    private resolveTemplate(finStatements: IFinStatements[], targetIndex: number): IAccountMappingTemplate | null {
        const isFinStatementsDefined = finStatements != null;
        const isFinStatementsLengthValid = finStatements.length >= targetIndex + 1;
        const isTargetFinStatementDefined = finStatements[targetIndex] != null;
        const isTemplateAndAccountsMappingDefined =
            isTargetFinStatementDefined && finStatements[targetIndex].templateAndAccountsMapping != null;
        if (
            isFinStatementsDefined &&
            isFinStatementsLengthValid &&
            isTargetFinStatementDefined &&
            isTemplateAndAccountsMappingDefined
        ) {
            return finStatements[targetIndex].templateAndAccountsMapping.template;
        } else {
            return null;
        }
    }

    private mapFinancialStatementSourceForSaving(finSource: IFinancialStatementSource): IFinancialStatementSource {
        const mappedSource: IFinancialStatementSource = {
            importDefinition: finSource.importDefinition,
            interpretation: finSource.interpretation,
            accountsFilter: finSource.accountsFilter,
            singleSheetAccountsRanges: finSource.singleSheetAccountsRanges,
            type: finSource.type,
        };
        return mappedSource;
    }

    private getIndexForFinancialStatementSourceFromActiveAnnualReport(
        finStatements: IFinStatements[],
        financialStatementType: FinancialStatementTypes,
    ): number {
        return finStatements.findIndex(
            (financialStatement) => financialStatement != null && financialStatement.type === financialStatementType,
        );
    }

    onImportFormCompletion(isFormCompleted: boolean) {
        debug('isFormCompleted', isFormCompleted);
        this.disableProceedButton = !isFormCompleted;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !isFormCompleted;
    }

    onAuditFileChange(event: boolean) {
        this.mixpanelService.track(MIXPANEL_EVENTS.UPLOAD_SHEET_SUCCESS);
        this.fileChanged = event;
        if (event) {
            // An Excel file has been selected — mark that the next save will be Excel-sourced.
            this.excelFileUploaded = true;
        } else {
            // The user removed the uploaded Excel via the trash icon. Any AI fix
            // running on the previous file is now meaningless — reset all AI
            // state so the panel disappears (and unblocks the UI which would
            // otherwise be stuck on "Agent arbeitet…" forever).
            this.resetAiState();
        }
        this.appStateDispatcher.setUploadedFileChanged(event);
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (
            this.activeAnnualReport != null &&
            (this.activeAnnualReport.finStatements == null || this.activeAnnualReport.finStatements.length === 0) &&
            this.disableProceedButton === false
        ) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        }
        return of(this.isUnsavedChangesDetected());
    }

    private detectChangesInImportDefinition(): boolean {
        if (this.activeAnnualReport == null) {
            return false;
        }
        if (this.activeFinStatements == null) {
            return false;
        }

        // we omit the property 'file' because it the uploaded file doesn't get saved
        let modifiedBalanceFinStatement = cloneDeep<IFinancialStatementSource>(
            this.financialStatementSource[TemplateType.BALANCE],
        );
        // modifiedBalanceFinStatement is with {} when its undefined
        // and savedBalanceFinStatement is with null when it is undefined
        // So we need to make the both null so that they would work in fastDeepEqual
        if (modifiedBalanceFinStatement != null && Object.keys(modifiedBalanceFinStatement).length === 0) {
            modifiedBalanceFinStatement = null;
        }

        let modifiedIncomeFinStatement = cloneDeep<IFinancialStatementSource>(
            this.financialStatementSource[TemplateType.INCOME],
        );

        if (modifiedBalanceFinStatement != null && Object.keys(modifiedIncomeFinStatement).length === 0) {
            modifiedIncomeFinStatement = null;
        }

        // saved data
        const savedBalanceFinStatement = cloneDeep<IFinancialStatementSource>(
            this.activeFinStatements[this.balanceIndexInFinStatementsArray]
                ? this.activeFinStatements[this.balanceIndexInFinStatementsArray].import
                : null,
        );

        const savedIncomeFinStatement = cloneDeep<IFinancialStatementSource>(
            this.activeFinStatements[this.incomeIndexInFinStatementsArray]
                ? (omit<IFinancialStatementSource>(
                      this.activeFinStatements[this.incomeIndexInFinStatementsArray].import,
                      ['file.file'],
                  ) as IFinancialStatementSource)
                : null,
        );

        if (this.isAutoFillApplied === false) {
            const isValueChanged = !(
                fastDeepEqual(modifiedBalanceFinStatement, savedBalanceFinStatement) &&
                fastDeepEqual(modifiedIncomeFinStatement, savedIncomeFinStatement) &&
                !this.fileChanged
            );
            return isValueChanged;
        } else {
            return this.fileChanged;
        }
    }

    isUnsavedChangesDetected(): boolean {
        return this.detectChangesInImportDefinition();
    }

    protected onSaveSuccessfully(): void {
        this.isSaveRequestInProgress = false;
        if (this.activeFinStatements != null) {
            // Update the activeFinStatements with the saved data so that further change detection works correctly
            this.activeFinStatements[this.balanceIndexInFinStatementsArray] = cloneDeep<IFinStatements>(
                this.financialStatementSource[FinancialStatementTypes.BALANCE],
            );
            this.activeFinStatements[this.incomeIndexInFinStatementsArray] = cloneDeep<IFinStatements>(
                this.financialStatementSource[FinancialStatementTypes.INCOME],
            );
        }
      
      // If saved data came from an Excel upload (not Bexio), clear lastSyncAt in the store
        // so the Bexio card shows "not synced" rather than a stale Bexio timestamp.
        if (this.excelFileUploaded) {
            this.excelFileUploaded = false;
        }
      
        this.savedSuccessfully.next(true);
        this.fireSuccessNotification();
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    onSelectAnnualReportForUploadAndMappingAutoFill(value: Record<AutofillOptions, string>): void {
        this.uploadAutofillWithOptions = value;

        this.uploadAndMappingAutoFillAnnualReportSubject.next(value);
        const annualReportEncryptedId = this.uploadAutofillWithOptions?.[TAXONAMY];
        if (annualReportEncryptedId != null) {
            this.annualReportProvider.dispatchGetAnnualReportForMappingAutoFill(annualReportEncryptedId);
        }
    }

    setEnforceOverwrite(value: boolean) {
        // REV-18627(C1d/R6): a shell follow-up report (activeFinStatements === []) is the
        // exact case the dialog must reach — only the NPE guard (== null) stays.
        if (this.activeFinStatements == null) {
            return;
        }
        this.importService.setEnforceOverwriteStructure(value);
    }
}
